// Expand the React import to pull in hooks needed for virtualization and refs.
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import closeButtonSrc from "../assets/legendarium/button_close.png";
import buttonArrowSrc from "../assets/button_arrow.png";
import tabSrc from "../assets/legendarium/tab.png";
import cardUiTopSrc from "../assets/legendarium/card_ui_top.png";
import cardUiBottomSrc from "../assets/legendarium/card_ui_bottom.png";

import cultistEnemyImg from "../assets/enemy.png";
import hexbinderEnemyImg from "../assets/enemy_hexbinder.png";
import nestedHorrorEnemyImg from "../assets/enemy_nested_horror.png";
import dreadbrandEnemyImg from "../assets/enemy_dreadbrand.png";
import corruptedCultistEnemyImg from "../assets/enemy_corrupted_cultist.png";
import yahakaEnemyImg from "../assets/enemy_yahaka.png";
import yahakaEnemy2Img from "../assets/enemy_yahaka_2.png";
import yahakaEnemy3Img from "../assets/enemy_yahaka_3.png";
import yahakaEnemy4Img from "../assets/enemy_yahaka_4.png";
import chainedPenitentEnemyImg from "../assets/enemy_chained_penitent.png";
import monsignorEnemyImg from "../assets/enemy_monsignor.png";
import darkApostleEnemyImg from "../assets/enemy_dark_apostle.png";
import chymistEnemyImg from "../assets/enemy_chymist.png";
import mercenaryEnemyImg from "../assets/enemy_mercenary.png";
import lampKeeperEnemyImg from "../assets/enemy_lamp_keeper.png";
import thresholdVerifierEnemyImg from "../assets/enemy_threshold_verifier.png";
import custodianEnemyImg from "../assets/enemy_custodian.png";
import blackhandEnemyImg from "../assets/enemy_blackhand.png";
import primeVesselEnemyImg from "../assets/enemy_prime_vessel.png";
import astralReliquaryImg from "../assets/machine_astral_reliquary.png";
import primeCrucibleImg from "../assets/machine_prime_crucible_1.png";
import pulseEngineImg from "../assets/machine_pulse_engine.png";
import synapticRetortImg from "../assets/machine_synaptic_retort.png";

import enemyUiSrc from "../combat/assets/enemy_UI.png";

import { fetchCardsWithCache, type RawCard } from "../combat/api/cards";
import { ENEMIES } from "../combat/game/enemies";
import { relicsPool } from "../game/data/relics";
import { tarotCards } from "../tarot/cards";
import {
  LORE_FRAGMENT_STORIES,
  LORE_SNIPPETS,
} from "../game/lore/loreSystem";
// Import the legendarium state reader along with the custom event and key constants.
import {
  readLegendariumState,
  LEGENDARIUM_EVENT,
  LEGENDARIUM_KEY,
} from "../game/persistence/legendariumStorage";
import { pickStableLoreIllustration } from "../utils/loreAssets";
import loreEntryBaseSrc from "../assets/legendarium/entry_base.png";
import loreEntryFullSrc from "../assets/legendarium/entry_full.png";

type TabKey = "enemies" | "relics" | "cards" | "tarot" | "lore";

type LegendariumProps = {
  onClose: () => void;
};

type LorePage = {
  id: string;
  label: string;
  text: string;
  displayText: string;
  discovered: boolean;
  kind: "story" | "snippet" | "misc";
};

type LoreEntryRecord = {
  id: string;
  title: string;
  numeral: string;
  image: string | null;
  pages: LorePage[];
  discovered: boolean;
  discoveredCount: number;
  totalCount: number;
  showPartTracker: boolean;
  trackerCount: number;
  badgeText?: string;
  previewText?: string;
  entryType: "story" | "snippet" | "misc";
};

type SelectedEntry =
  | { kind: "enemy"; entry: any }
  | { kind: "relic"; entry: any }
  | { kind: "card"; entry: any }
  | { kind: "tarot"; entry: any }
  | { kind: "lore"; entry: LoreEntryRecord }
  | null;

const FONT_FAMILY = "Karma, ui-serif, Georgia, serif";
const PANEL_BG =
  "linear-gradient(180deg, rgba(28,28,31,0.98) 0%, rgba(12,12,14,0.98) 100%)";
const COMBAT_CARD_ASPECT = "288 / 420";
const TAROT_CARD_ASPECT = "288 / 432";
const LEGENDARIUM_TAB_ASPECT = "312 / 102";
const SCREEN_CORNER_BUTTON_TOP = "clamp(18px, 1.55vw, 22px)";
const SCREEN_CORNER_BUTTON_RIGHT = "clamp(20px, 2vw, 28px)";
const SCREEN_CORNER_BUTTON_SIZE = "min(4.2vw, 68px)";
const SCREEN_CORNER_BUTTON_MIN_SIZE = 40;

// --- Virtualization configuration constants ---
// How many extra rows of items should be rendered above and below the viewport.
const VIRTUAL_GRID_OVERSCAN_ROWS = 2;
// When the number of rows in a grid is below this threshold, don't bother with virtualization.
const VIRTUAL_GRID_FALLBACK_MIN_ROWS = 3;

// --- Scroll viewport hook ---
// Tracks scroll position and container size with requestAnimationFrame scheduling. When disabled,
// it returns a zeroed viewport to avoid unnecessary work.
type ScrollViewportState = {
  scrollTop: number;
  width: number;
  height: number;
};

function useScrollViewport(
  scrollContainerRef: React.RefObject<HTMLDivElement>,
  enabled: boolean,
) {
  const [viewport, setViewport] = useState<ScrollViewportState>({
    scrollTop: 0,
    width: 0,
    height: 0,
  });

  useLayoutEffect(() => {
    // If virtualization is disabled, reset the viewport to zero once and return.
    if (!enabled) {
      setViewport((current) =>
        current.scrollTop === 0 && current.width === 0 && current.height === 0
          ? current
          : { scrollTop: 0, width: 0, height: 0 },
      );
      return undefined;
    }

    const node = scrollContainerRef.current;
    if (!node) return undefined;

    let rafId = 0;

    const syncViewport = () => {
      rafId = 0;
      setViewport((current) => {
        const next = {
          scrollTop: node.scrollTop,
          width: node.clientWidth,
          height: node.clientHeight,
        };
        return current.scrollTop === next.scrollTop &&
          current.width === next.width &&
          current.height === next.height
          ? current
          : next;
      });
    };

    const scheduleViewportSync = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(syncViewport);
    };

    // Initial sync
    syncViewport();

    node.addEventListener("scroll", scheduleViewportSync, { passive: true });
    window.addEventListener("resize", scheduleViewportSync);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleViewportSync)
        : null;

    resizeObserver?.observe(node);

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      node.removeEventListener("scroll", scheduleViewportSync);
      window.removeEventListener("resize", scheduleViewportSync);
      resizeObserver?.disconnect();
    };
  }, [enabled, scrollContainerRef]);

  return viewport;
}

// --- Generic virtualized grid component ---
// Renders a collection of items in a responsive grid, only mounting the rows that are visible.
// When virtualization is disabled or the list is small, it falls back to a normal CSS grid.
type VirtualizedGridProps<T> = {
  items: T[];
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  enabled: boolean;
  minColumnWidth: number;
  columnGap: number;
  rowGap: number;
  overscanRows?: number;
  getItemKey: (item: T, index: number) => string;
  getItemHeight: (columnWidth: number) => number;
  renderItem: (item: T, index: number) => React.ReactNode;
};

function VirtualizedGrid<T>({
  items,
  scrollContainerRef,
  enabled,
  minColumnWidth,
  columnGap,
  rowGap,
  overscanRows = VIRTUAL_GRID_OVERSCAN_ROWS,
  getItemKey,
  getItemHeight,
  renderItem,
}: VirtualizedGridProps<T>) {
  const viewport = useScrollViewport(scrollContainerRef, enabled);

  // Determine the width and height of the scroll container; clamp to non-negative values.
  const width = Math.max(0, viewport.width);
  const height = Math.max(0, viewport.height);

  // Compute the number of columns that fit given the minimum column width and gap.
  const columns =
    width > 0
      ? Math.max(
          1,
          Math.floor((width + columnGap) / (minColumnWidth + columnGap)),
        )
      : 1;

  // Compute the available width for columns excluding gaps.
  const totalGapWidth = columnGap * Math.max(0, columns - 1);
  const columnWidth =
    width > 0 ? Math.max(0, (width - totalGapWidth) / columns) : minColumnWidth;

  // Compute the height of each row based on the computed column width.
  const itemHeight = getItemHeight(columnWidth);
  const rowStride = itemHeight + rowGap;
  const rowCount = Math.ceil(items.length / columns);

  // If virtualization is disabled or the list is small, render all items in a normal CSS grid.
  const shouldFallbackToPlainGrid =
    !enabled ||
    width <= 0 ||
    height <= 0 ||
    items.length <= columns * VIRTUAL_GRID_FALLBACK_MIN_ROWS;

  if (shouldFallbackToPlainGrid) {
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
          columnGap,
          rowGap,
        }}
      >
        {items.map((item, index) => (
          <div key={getItemKey(item, index)} style={{ minWidth: 0 }}>
            {renderItem(item, index)}
          </div>
        ))}
      </div>
    );
  }

  // Determine which rows of items should be rendered given the current scroll position.
  const startRow = Math.max(
    0,
    Math.floor(viewport.scrollTop / Math.max(1, rowStride)) - overscanRows,
  );
  const endRow = Math.min(
    rowCount,
    Math.ceil((viewport.scrollTop + height) / Math.max(1, rowStride)) +
      overscanRows,
  );

  const startIndex = startRow * columns;
  const endIndex = Math.min(items.length, endRow * columns);
  const totalHeight = Math.max(0, rowCount * rowStride - rowGap);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: totalHeight,
      }}
    >
      {items.slice(startIndex, endIndex).map((item, offset) => {
        const index = startIndex + offset;
        const row = Math.floor(index / columns);
        const column = index % columns;

        return (
          <div
            key={getItemKey(item, index)}
            style={{
              position: "absolute",
              top: row * rowStride,
              left: column * (columnWidth + columnGap),
              width: columnWidth,
              height: itemHeight,
            }}
          >
            {renderItem(item, index)}
          </div>
        );
      })}
    </div>
  );
}

function romanize(input: number) {
  const value = Math.max(1, Math.floor(Number(input) || 1));
  const map = [
    [1000, "M"],
    [900, "CM"],
    [500, "D"],
    [400, "CD"],
    [100, "C"],
    [90, "XC"],
    [50, "L"],
    [40, "XL"],
    [10, "X"],
    [9, "IX"],
    [5, "V"],
    [4, "IV"],
    [1, "I"],
  ] as const;

  let n = value;
  let out = "";
  for (const [amount, token] of map) {
    while (n >= amount) {
      out += token;
      n -= amount;
    }
  }
  return out;
}


function normalizeLoreText(value: unknown): string {
  return String(value ?? "")
    .replace(/\r\n?/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function buildStoryLorePageText(storyTitle: unknown, part: any): string {
  const safeTitle = String(storyTitle || "Lore Fragment").trim();
  const safeLabel = String(part?.label || "Fragment").trim();
  const safeBody = String(part?.body || "").trim();

  if (!safeBody) return `${safeTitle}\n${safeLabel}`;
  return `${safeTitle}\n${safeLabel}\n\n${safeBody}`;
}

function buildSnippetLabel(index: number): string {
  return `Snippet ${romanize(index + 1)}`;
}

function truncateLorePreview(value: unknown, maxLength = 160): string {
  const normalized = normalizeLoreText(value)
    .replace(/\s*\n+\s*/g, " ")
    .trim();
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength).trimEnd()}…`;
}

function categorizeMiscLoreText(text: string): "secret" | "door" | "misc" {
  const normalized = normalizeLoreText(text).toLowerCase();

  if (
    normalized.includes("stone remembers more than flesh") ||
    normalized.includes(" hides something")
  ) {
    return "secret";
  }

  if (/(door|keyhole|handle|lock|chain|wood|sigil|barred|claw)/i.test(normalized)) {
    return "door";
  }

  return "misc";
}

function getInitialLorePageIndex(entry: LoreEntryRecord | null | undefined): number {
  if (!entry?.pages?.length) return 0;
  const firstDiscoveredIndex = entry.pages.findIndex((page) => page.discovered);
  return firstDiscoveredIndex >= 0 ? firstDiscoveredIndex : 0;
}

function getLoreEntryImage(entry: Pick<LoreEntryRecord, "id" | "pages">): string | null {
  const discoveredPage = entry.pages.find((page) => page.discovered);
  const imageKey = discoveredPage?.text || `${entry.id}:cover`;
  return pickStableLoreIllustration(imageKey);
}

function getLorePageImage(entry: LoreEntryRecord, page: LorePage): string | null {
  return pickStableLoreIllustration(
    page.discovered ? page.text : `${entry.id}:${page.id}`,
  );
}

function cardIdOf(card: RawCard | any): string {
  return String(card?.id ?? card?._id ?? "").trim();
}

function cardTitleOf(card: RawCard | any): string {
  return String(card?.title ?? card?.name ?? "Unknown Card").trim();
}

function cardSubtitleOf(card: RawCard | any): string {
  return String(card?.subtitle ?? "").trim();
}

function isLegendariumPlayerCard(card: RawCard | any): boolean {
  const id = cardIdOf(card);
  const kind = String((card as any)?.kind ?? "")
    .trim()
    .toLowerCase();
  const subtitle = cardSubtitleOf(card).toLowerCase();
  const specialType = String((card as any)?.specialType ?? "")
    .trim()
    .toLowerCase();

  if (!id) return false;
  if (kind === "enemy") return false;
  if ((card as any)?.acquisitionLocked) return false;
  if (specialType === "affliction") return false;
  if (/(^|\b)enemy(\b|$)/i.test(subtitle)) return false;

  return true;
}

function cardImageOf(card: RawCard | any): string {
  return String(
    card?.cardUrl ?? card?.imageUrl ?? card?.img ?? card?.image ?? "",
  ).trim();
}

function legendariumEnemyCardIdentityOf(card: RawCard | any): string {
  const id = cardIdOf(card);
  if (id) return id;

  const title = cardTitleOf(card).toLowerCase();
  if (title) return `title:${title}`;

  return "";
}

function resolveLegendariumEnemyCards(
  enemyCards: Array<{ id: string; title?: string }> | undefined,
  cardById: Map<string, RawCard>,
): Array<RawCard | { id: string; title: string; subtitle: string; description: string }> {
  const resolvedCards: Array<
    RawCard | { id: string; title: string; subtitle: string; description: string }
  > = [];
  const seen = new Set<string>();

  for (const enemyCard of enemyCards || []) {
    const resolved = cardById.get(String(enemyCard?.id || "").trim());
    const resolvedCard = resolved
      ? resolved
      : {
          id: enemyCard.id,
          title: enemyCard.title || "Unknown Card",
          subtitle: "Enemy Card",
          description: "",
        };

    const identity = legendariumEnemyCardIdentityOf(resolvedCard);
    if (!identity || seen.has(identity)) continue;

    seen.add(identity);
    resolvedCards.push(resolvedCard);
  }

  return resolvedCards;
}

function stripHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/<\/?p[^>]*>/gi, "\n\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function stripHtmlInline(value: unknown): string {
  return stripHtml(value)
    .replace(/\s*\n+\s*/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

type CardKeywordItem = {
  key: "Exile" | "Discard" | "Chain" | "Stun" | "Addendum" | "Delay";
  label: string;
  description: string;
};

const CARD_KEYWORD_COPY: Record<CardKeywordItem["key"], CardKeywordItem> = {
  Exile: {
    key: "Exile",
    label: "Exile",
    description: "Exits the deck after played.",
  },
  Discard: {
    key: "Discard",
    label: "Discard",
    description: "Remove a card from your hand.",
  },
  Chain: {
    key: "Chain",
    label: "Chain",
    description: "Play another card after this one.",
  },
  Stun: {
    key: "Stun",
    label: "Stun",
    description:
      "A stunned fighter skips their turn and cannot draw, play, Focus, or Recast.",
  },
  Addendum: {
    key: "Addendum",
    label: "Addendum",
    description: "Adds an extra effect to the base card for an extra cost.",
  },
  Delay: {
    key: "Delay",
    label: "Delay",
    description: "Delayed effects or costs resolve in later turns.",
  },
};

function cardHasEffect(card: RawCard | any, type: string) {
  return (
    Array.isArray(card?.effects) &&
    card.effects.some((effect: any) => effect?.type === type)
  );
}

function isLegendariumMagicCard(card: RawCard | any) {
  const magicLikeText = [
    card?.kind,
    (card as any)?.type,
    (card as any)?.cardType,
    (card as any)?.category,
    (card as any)?.family,
    card?.subtitle,
    (card as any)?.frame,
    (card as any)?.border,
    (card as any)?.palette,
    (card as any)?.color,
  ]
    .map((value) =>
      String(value ?? "")
        .trim()
        .toLowerCase(),
    )
    .filter(Boolean)
    .join(" ");

  return /(^|\b)(magic|dark|black)(\b|$)/i.test(magicLikeText);
}

function getLegendariumCardKeywordItems(
  card: RawCard | any,
): CardKeywordItem[] {
  const normalizedKeywords = Array.isArray(card?.keywords)
    ? card.keywords
    : typeof card?.keywords === "string"
      ? card.keywords.split(/[;,|]/)
      : [];

  const keywords = new Set(
    normalizedKeywords
      .map((keyword) => stripHtml(keyword).trim())
      .filter(Boolean),
  );

  const fallbackText = [
    card?.keywords,
    card?.description,
    card?.subtitle,
    (card as any)?.text,
    (card as any)?.rulesText,
    (card as any)?.oracleText,
    (card as any)?.tooltipText,
    (card as any)?.effectText,
    (card as any)?.addendum,
    Array.isArray(card?.effects) ? JSON.stringify(card.effects) : "",
    Array.isArray((card as any)?.delayed)
      ? JSON.stringify((card as any).delayed)
      : "",
    JSON.stringify(card ?? {}),
  ]
    .map((value) => stripHtml(value))
    .join("\n");

  const normalizedFallbackText = fallbackText
    .replace(/[(){}\[\].,:;!?]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const hasKeywordText = (keyword: CardKeywordItem["key"]) =>
    new RegExp(`(^|\\W)${keyword}(?=\\W|$)`, "i").test(normalizedFallbackText);

  const items: CardKeywordItem[] = [];
  const implicitMagicChain = isLegendariumMagicCard(card);

  if (keywords.has("Exile") || hasKeywordText("Exile"))
    items.push(CARD_KEYWORD_COPY.Exile);
  if (cardHasEffect(card, "discard") || hasKeywordText("Discard"))
    items.push(CARD_KEYWORD_COPY.Discard);
  if (keywords.has("Chain") || implicitMagicChain || hasKeywordText("Chain"))
    items.push(CARD_KEYWORD_COPY.Chain);
  if (cardHasEffect(card, "stun") || hasKeywordText("Stun"))
    items.push(CARD_KEYWORD_COPY.Stun);
  if (card?.addendum || hasKeywordText("Addendum"))
    items.push(CARD_KEYWORD_COPY.Addendum);
  if (
    Number(card?.delayTurns ?? 0) > 0 ||
    (card?.delayed?.length ?? 0) > 0 ||
    Number((card as any)?.costDelayTurns ?? 0) > 0 ||
    hasKeywordText("Delay")
  ) {
    items.push(CARD_KEYWORD_COPY.Delay);
  }

  return items;
}

function enemyImageForId(enemyId: string) {
  if (enemyId === "cultist") return cultistEnemyImg;
  if (enemyId === "corrupted_cultist") return corruptedCultistEnemyImg;
  if (enemyId === "hexbinder") return hexbinderEnemyImg;
  if (enemyId === "nested_horror") return nestedHorrorEnemyImg;
  if (enemyId === "dreadbrand") return dreadbrandEnemyImg;
  if (enemyId === "chained-penitent") return chainedPenitentEnemyImg;
  if (enemyId === "monsignor") return monsignorEnemyImg;
  if (enemyId === "dark-apostle") return darkApostleEnemyImg;
  if (enemyId === "chymist") return chymistEnemyImg;
  if (enemyId === "mercenary") return mercenaryEnemyImg;
  if (enemyId === "lamp_keeper") return lampKeeperEnemyImg;
  if (enemyId === "threshold_verifier") return thresholdVerifierEnemyImg;
  if (enemyId === "custodian") return custodianEnemyImg;
  if (enemyId === "blackhand") return blackhandEnemyImg;
  if (enemyId === "prime_vessel") return primeVesselEnemyImg;
  if (enemyId === "prime_crucible_device") return primeCrucibleImg;
  if (enemyId === "pulse_engine_device") return pulseEngineImg;
  if (enemyId === "synaptic_retort_device") return synapticRetortImg;
  if (enemyId === "astral_reliquary_device") return astralReliquaryImg;

  if (enemyId.startsWith("yahaka_voidfeeder")) {
    const suffix = Math.max(
      1,
      Math.floor(Number(enemyId.split("_").pop()) || 1),
    );
    if (suffix >= 7) return yahakaEnemy4Img;
    if (suffix >= 5) return yahakaEnemy3Img;
    if (suffix >= 3) return yahakaEnemy2Img;
    return yahakaEnemyImg;
  }

  return cultistEnemyImg;
}

const LEGENDARIUM_ENEMY_ORDER = [
  "cultist",
  "corrupted_cultist",
  "chained-penitent",
  "hexbinder",
  "mercenary",
  "chymist",
  "dreadbrand",
  "dark-apostle",
  "threshold_verifier",
  "lamp_keeper",
  "custodian",
  "yahaka_voidfeeder",
  "yahaka_voidfeeder_1",
  "yahaka_voidfeeder_2",
  "yahaka_voidfeeder_3",
  "yahaka_voidfeeder_4",
  "yahaka_voidfeeder_5",
  "yahaka_voidfeeder_6",
  "yahaka_voidfeeder_7",
  "nested_horror",
  "monsignor",
  "blackhand",
  "prime_vessel",
  "prime_crucible_device",
  "pulse_engine_device",
  "synaptic_retort_device",
  "astral_reliquary_device",
] as const;

const LEGENDARIUM_ENEMY_ORDER_INDEX = new Map<string, number>(
  LEGENDARIUM_ENEMY_ORDER.map((enemyId, index) => [enemyId, index]),
);

function getLegendariumEnemySortKey(enemy: any, fallbackIndex: number) {
  const enemyId = String(enemy?.id ?? "").trim();
  const exactMatch = LEGENDARIUM_ENEMY_ORDER_INDEX.get(enemyId);
  if (typeof exactMatch === "number") return exactMatch;

  if (enemyId.startsWith("yahaka_voidfeeder")) {
    const suffix = Number(enemyId.split("_").pop());
    if (Number.isFinite(suffix) && suffix >= 1) {
      return LEGENDARIUM_ENEMY_ORDER_INDEX.get("yahaka_voidfeeder")! + suffix;
    }
    return LEGENDARIUM_ENEMY_ORDER_INDEX.get("yahaka_voidfeeder") ?? fallbackIndex;
  }

  return LEGENDARIUM_ENEMY_ORDER.length + fallbackIndex;
}

function capitalize(value: unknown) {
  const text = String(value ?? "").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
}

function enemyDescriptionOf(enemy: any): string {
  const explicit = stripHtml(
    enemy?.description ??
      enemy?.lore ??
      enemy?.flavorText ??
      enemy?.legendariumDescription ??
      "",
  );
  if (explicit) return explicit;

  const enemyId = String(enemy?.id ?? "").trim();

  if (enemyId === "cultist") {
    return "A knife-bearing zealot that relies on direct pressure and relentless basic attacks.";
  }
  if (enemyId === "corrupted_cultist") {
    return "A cultist body overtaken by corruption, tougher and far more vicious than its original form.";
  }
  if (enemyId === "hexbinder") {
    return "A ritual caster that controls the fight through curses, attrition, and occult manipulation.";
  }
  if (enemyId === "nested_horror") {
    return "A malformed eldritch mass built to endure punishment and answer with disturbing force.";
  }
  if (enemyId === "chymist") {
    return "An occult scientist who cycles unstable compounds, corrosive vapors, and volatile reactions to pressure the player over time.";
  }
  if (enemyId === "mercenary") {
    return "A disciplined killer who alternates patient setup with precise shots, deceptive feints, and armor-piercing ambush strikes.";
  }
  if (enemyId === "lamp_keeper") {
    return "A keeper of illumination who manipulates the Room Light, pressures HP and SP through shifting brightness, and sends the player back into the dungeon with altered torchlight.";
  }
  if (enemyId === "threshold_verifier") {
    return "A punitive tower sentinel that marks the player's deck with restrictions, then escalates into harsh sanctions when its thresholds are broken.";
  }
  if (enemyId === "custodian") {
    return "A severe archivist of loss who turns forced discards into exile, catalogs the player's lost cards by function, and later cashes them out into damage, armor, and recovery before reprocessing them back into the deck.";
  }
  if (enemyId === "dreadbrand") {
    return "A stalking nightmare of fang and shadow that leans into savage, aggressive turns.";
  }
  if (enemyId === "monsignor") {
    return "A mid-run judge of guilt and obedience who corrupts the player's deck with Afflictions while laying down decrees.";
  }
  if (enemyId === "blackhand") {
    return "A ruthless executioner who chains momentum, precision strikes, and shadowed movement into relentless pressure.";
  }
  if (enemyId === "prime_vessel") {
    return "A failed occult-scientific vessel sustained by four external devices, growing weaker as its machinery is destroyed.";
  }
  if (enemyId === "prime_crucible_device") {
    return "A reinforced crucible node that powers the Prime Vessel's laboratory apparatus until the player destroys it.";
  }
  if (enemyId === "pulse_engine_device") {
    return "A rhythmic engine core embedded in the laboratory network, feeding strength into the Prime Vessel's system.";
  }
  if (enemyId === "synaptic_retort_device") {
    return "A cognition-forging retort that channels unstable thought and corrupted energy into the Prime Vessel.";
  }
  if (enemyId === "astral_reliquary_device") {
    return "A reliquary machine that stores and transfers occult power to the Prime Vessel from the edges of the laboratory.";
  }
  if (enemyId.startsWith("yahaka_voidfeeder")) {
    return "A sanity-born predator that manifests when the mind collapses, returning in stronger forms each time.";
  }

  return "An archived enemy entry recovered from the depths of the Legendarium.";
}

function useLegendariumData() {
  const [cards, setCards] = useState<RawCard[]>([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [cardsError, setCardsError] = useState("");
  const [discovered, setDiscovered] = useState(() => readLegendariumState());

  useEffect(() => {
    if (typeof window === "undefined") {
      setDiscovered(readLegendariumState());
      return undefined;
    }

    let mounted = true;
    let requestVersion = 0;
    let retryTimer: number | undefined;
    // No interval polling; legendarium changes will be handled via events.
    let hasLoadedCardsOnce = false;

    const clearRetry = () => {
      if (typeof retryTimer === "number") {
        window.clearTimeout(retryTimer);
        retryTimer = undefined;
      }
    };

    // Compare two arrays of strings for equality.
    const areSameStringArrays = (a: string[] = [], b: string[] = []) =>
      a.length === b.length && a.every((value, index) => value === b[index]);
    // Compare two legendarium state objects. Avoid re-rendering if nothing changed.
    const areSameLegendariumState = (a: any, b: any) =>
      areSameStringArrays(a?.enemies, b?.enemies) &&
      areSameStringArrays(a?.relics, b?.relics) &&
      areSameStringArrays(a?.cards, b?.cards) &&
      areSameStringArrays(a?.tarot, b?.tarot) &&
      areSameStringArrays(a?.lore, b?.lore) &&
      areSameStringArrays(a?.storylets, b?.storylets);
    const syncDiscovered = () => {
      if (!mounted) return;
      const nextState = readLegendariumState();
      setDiscovered((current) =>
        areSameLegendariumState(current, nextState) ? current : nextState,
      );
    };

    const loadCards = async (cacheTtlMs = 30 * 60 * 1000) => {
      const runId = ++requestVersion;
      clearRetry();
      if (mounted) {
        setCardsLoading(true);
        setCardsError("");
      }

      try {
        const loaded = await fetchCardsWithCache(cacheTtlMs);
        if (!mounted || runId !== requestVersion) return;

        const normalized = Array.isArray(loaded) ? loaded : [];
        if (normalized.length > 0) {
          hasLoadedCardsOnce = true;
        }

        setCards(normalized);
        setCardsError("");
        setCardsLoading(false);

        if (!hasLoadedCardsOnce && normalized.length === 0) {
          retryTimer = window.setTimeout(() => {
            void loadCards(0);
          }, 1200);
        }
      } catch {
        if (!mounted || runId !== requestVersion) return;

        if (!hasLoadedCardsOnce) {
          setCardsError("Combat cards could not be loaded right now.");
        }
        setCardsLoading(false);

        retryTimer = window.setTimeout(() => {
          void loadCards(0);
        }, 1600);
      }
    };

    // When regaining focus or visibility, only update discovery progress. Avoid
    // forcing card re-fetches on focus/visibility changes; cards are loaded on
    // mount and via retry logic.
    const refreshLegendariumProgress = () => {
      syncDiscovered();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshLegendariumProgress();
      }
    };

    syncDiscovered();
    void loadCards();

    // Respond to focus/visibility changes without forcing a card reload.
    window.addEventListener("focus", refreshLegendariumProgress);

    // Only respond to legendarium-related storage changes. Use a dedicated handler to filter on key.
    const handleStorage = (event: StorageEvent) => {
      if (event.key && event.key !== LEGENDARIUM_KEY) return;
      syncDiscovered();
    };
    window.addEventListener("storage", handleStorage);

    // Listen for the custom legendarium event dispatched when the state changes.
    window.addEventListener(LEGENDARIUM_EVENT, syncDiscovered as EventListener);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      mounted = false;
      clearRetry();
      window.removeEventListener("focus", refreshLegendariumProgress);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(LEGENDARIUM_EVENT, syncDiscovered as EventListener);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return {
    discovered,
    cards,
    cardsLoading,
    cardsError,
  };
}

function StatLine({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        fontFamily: FONT_FAMILY,
        fontSize: 18,
      }}
    >
      <span style={{ color: "rgba(240,240,240,0.75)", fontWeight: 400 }}>
        {label}
      </span>
      <span style={{ color: "#f4f0e7", fontWeight: 700 }}>{value}</span>
    </div>
  );
}

function LegendariumArrow({
  flipped = false,
  dimmed = false,
}: {
  flipped?: boolean;
  dimmed?: boolean;
}) {
  const stroke = dimmed ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.92)";

  return (
    <svg
      viewBox="0 0 64 96"
      aria-hidden
      focusable="false"
      style={{
        width: "100%",
        height: "100%",
        display: "block",
        transform: flipped ? "scaleX(-1)" : "none",
      }}
    >
      <path
        d="M44 14 L20 48 L44 82"
        fill="none"
        stroke={stroke}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M39 21 L28 48 L39 75"
        fill="none"
        stroke={stroke}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.85"
      />
      <path
        d="M28 48 H52"
        fill="none"
        stroke={stroke}
        strokeWidth="1.8"
        strokeLinecap="round"
        opacity="0.9"
      />
      <circle
        cx="46"
        cy="48"
        r="2.6"
        fill="none"
        stroke={stroke}
        strokeWidth="1.4"
        opacity="0.9"
      />
      <path
        d="M52 35 v-6 M52 67 v-6"
        fill="none"
        stroke={stroke}
        strokeWidth="1.4"
        strokeLinecap="round"
        opacity="0.8"
      />
    </svg>
  );
}

export default function LegendariumScreen({ onClose }: LegendariumProps) {
  const [activeTab, setActiveTab] = useState<TabKey>("enemies");
  const [selectedEntry, setSelectedEntry] = useState<SelectedEntry>(null);
  const [selectedLorePageIndex, setSelectedLorePageIndex] = useState(0);
  const [hoveredEnemyCard, setHoveredEnemyCard] = useState<string | null>(null);
  const [hoveredEnemyMiniCard, setHoveredEnemyMiniCard] = useState<
    string | null
  >(null);
  const [hoveredLoreCard, setHoveredLoreCard] = useState<string | null>(null);
  const [viewportSize, setViewportSize] = useState(() => ({
    width: typeof window !== "undefined" ? window.innerWidth : 1920,
    height: typeof window !== "undefined" ? window.innerHeight : 1080,
  }));
  // Ref for the scrollable content area. Used by the VirtualizedGrid to track scroll position and container size.
  const contentScrollRef = useRef<HTMLDivElement | null>(null);
  const { discovered, cards, cardsLoading, cardsError } = useLegendariumData();

  const cardById = useMemo(() => {
    const next = new Map<string, RawCard>();
    for (const card of cards) {
      const id = cardIdOf(card);
      if (id) next.set(id, card);
    }
    return next;
  }, [cards]);

  const enemyEntries = useMemo(() => {
    return [...ENEMIES]
      .map((enemy, index) => ({ enemy, originalIndex: index }))
      .sort((a, b) => {
        const aKey = getLegendariumEnemySortKey(a.enemy, a.originalIndex);
        const bKey = getLegendariumEnemySortKey(b.enemy, b.originalIndex);
        if (aKey !== bKey) return aKey - bKey;
        return a.originalIndex - b.originalIndex;
      })
      .map(({ enemy }, index) => ({
        ...enemy,
        image: enemyImageForId(enemy.id),
        numeral: romanize(index + 1),
        discovered: discovered.enemies.includes(enemy.id),
        resolvedCards: resolveLegendariumEnemyCards(enemy.enemyCards, cardById),
      }));
  }, [cardById, discovered.enemies]);

  const unlockedEnemyEntries = useMemo(
    () => enemyEntries.filter((entry) => entry.discovered),
    [enemyEntries],
  );

  const relicEntries = useMemo(() => {
    return relicsPool.map((relic, index) => ({
      ...relic,
      numeral: romanize(index + 1),
      description: stripHtml(relic.description),
      discovered: discovered.relics.includes(relic.id),
    }));
  }, [discovered.relics]);

  const unlockedRelicEntries = useMemo(
    () => relicEntries.filter((entry) => entry.discovered),
    [relicEntries],
  );

  const tarotEntries = useMemo(() => {
    return tarotCards.map((card, index) => ({
      ...card,
      numeral: romanize(index + 1),
      discovered: discovered.tarot.includes(card.id),
      uprightTitle: String(
        card.orientations?.right?.title || `${card.name} (Upright)`,
      ).trim(),
      uprightDescription: stripHtml(card.orientations?.right?.description || ""),
      uprightPermanent: !!card.orientations?.right?.permanent,
      reversedTitle: String(
        card.orientations?.wrong?.title || `${card.name} (Reversed)`,
      ).trim(),
      reversedDescription: stripHtml(card.orientations?.wrong?.description || ""),
      reversedPermanent: !!card.orientations?.wrong?.permanent,
    }));
  }, [discovered.tarot]);

  const unlockedTarotEntries = useMemo(
    () => tarotEntries.filter((entry) => entry.discovered),
    [tarotEntries],
  );

  const loreEntries = useMemo<LoreEntryRecord[]>(() => {
    const discoveredLoreTexts = new Set(
      (Array.isArray(discovered.lore) ? discovered.lore : [])
        .map((entry) => normalizeLoreText(entry))
        .filter(Boolean),
    );
    const matchedLoreTexts = new Set<string>();

    const storyEntries = LORE_FRAGMENT_STORIES.map((story, storyIndex) => {
      const storyId = String(story?.id || `story-${storyIndex}`);
      const storyTitle = String(story?.title || `Lore Fragment ${storyIndex + 1}`);
      const parts = Array.isArray(story?.parts) ? story.parts : [];
      const pages: LorePage[] = parts.map((part, partIndex) => {
        const fullText = normalizeLoreText(buildStoryLorePageText(storyTitle, part));
        const discoveredPart = discoveredLoreTexts.has(fullText);
        if (discoveredPart) matchedLoreTexts.add(fullText);
        return {
          id: `${storyId}:${String(part?.id || `part-${partIndex}`)}`,
          label: String(part?.label || `Part ${partIndex + 1}`),
          text: fullText,
          displayText: String(part?.body || "").trim() || fullText,
          discovered: discoveredPart,
          kind: "story",
        };
      });

      const discoveredCount = pages.filter((page) => page.discovered).length;

      const baseEntry: LoreEntryRecord = {
        id: `story:${storyId}`,
        title: storyTitle,
        numeral: "",
        image: null,
        pages,
        discovered: discoveredCount > 0,
        discoveredCount,
        totalCount: Math.max(1, pages.length),
        showPartTracker: pages.length > 1,
        trackerCount: Math.max(1, pages.length),
        previewText: truncateLorePreview(
          (pages.find((page) => page.discovered) || pages[0])?.displayText || "",
        ),
        entryType: "story",
      };

      return {
        ...baseEntry,
        image: getLoreEntryImage(baseEntry),
      };
    });

    const snippetPages: LorePage[] = LORE_SNIPPETS.map((snippet, snippetIndex) => {
      const fullText = normalizeLoreText(snippet);
      const discoveredSnippet = discoveredLoreTexts.has(fullText);
      if (discoveredSnippet) matchedLoreTexts.add(fullText);
      return {
        id: `snippet:${snippetIndex}`,
        label: buildSnippetLabel(snippetIndex),
        text: fullText,
        displayText: fullText,
        discovered: discoveredSnippet,
        kind: "snippet",
      };
    });

    const snippetDiscoveredCount = snippetPages.filter((page) => page.discovered).length;
    const recoveredSnippetsEntry: LoreEntryRecord = {
      id: "snippets:recovered",
      title: "Recovered Snippets",
      numeral: "",
      image: null,
      pages: snippetPages,
      discovered: snippetDiscoveredCount > 0,
      discoveredCount: snippetDiscoveredCount,
      totalCount: Math.max(1, snippetPages.length),
      showPartTracker: false,
      trackerCount: 0,
      badgeText: `${snippetDiscoveredCount}/${Math.max(1, snippetPages.length)}`,
      previewText: truncateLorePreview(
        (snippetPages.find((page) => page.discovered) || snippetPages[0])?.displayText ||
          "",
      ),
      entryType: "snippet",
    };
    recoveredSnippetsEntry.image = getLoreEntryImage(recoveredSnippetsEntry);

    const miscBuckets: Record<"secret" | "door" | "misc", string[]> = {
      secret: [],
      door: [],
      misc: [],
    };

    for (const value of discoveredLoreTexts) {
      if (matchedLoreTexts.has(value)) continue;
      miscBuckets[categorizeMiscLoreText(value)].push(value);
    }

    const miscEntries: LoreEntryRecord[] = [
      {
        key: "secret",
        title: "Sanctified Whispers",
      },
      {
        key: "door",
        title: "Barred Door Whispers",
      },
      {
        key: "misc",
        title: "Recovered Marginalia",
      },
    ]
      .map(({ key, title }) => {
        const values = miscBuckets[key as "secret" | "door" | "misc"];
        if (!values.length) return null;

        const pages: LorePage[] = values.map((value, index) => ({
          id: `${key}:${index}`,
          label: `Record ${romanize(index + 1)}`,
          text: value,
          displayText: value,
          discovered: true,
          kind: "misc",
        }));

        const entry: LoreEntryRecord = {
          id: `misc:${key}`,
          title,
          numeral: "",
          image: null,
          pages,
          discovered: true,
          discoveredCount: pages.length,
          totalCount: pages.length,
          showPartTracker: false,
          trackerCount: 0,
          badgeText: `${pages.length}`,
          previewText: truncateLorePreview(values[0] || ""),
          entryType: "misc",
        };

        return {
          ...entry,
          image: getLoreEntryImage(entry),
        };
      })
      .filter(Boolean) as LoreEntryRecord[];

    return [...storyEntries, recoveredSnippetsEntry, ...miscEntries].map(
      (entry, index) => ({
        ...entry,
        numeral: romanize(index + 1),
      }),
    );
  }, [discovered.lore]);

  const unlockedLoreEntries = useMemo(
    () => loreEntries.filter((entry) => entry.discovered),
    [loreEntries],
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncViewportSize = () => {
      setViewportSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    syncViewportSize();
    window.addEventListener("resize", syncViewportSize);
    return () => window.removeEventListener("resize", syncViewportSize);
  }, []);

  const enemyCardIds = useMemo(() => {
    const next = new Set<string>();
    for (const enemy of ENEMIES) {
      for (const enemyCard of enemy.enemyCards || []) {
        const id = String(enemyCard?.id ?? "").trim();
        if (id) next.add(id);
      }
    }
    return next;
  }, []);

  const cardEntries = useMemo(() => {
    return [...cards]
      .filter((card) => {
        const id = cardIdOf(card);
        return isLegendariumPlayerCard(card) && !enemyCardIds.has(id);
      })
      .sort((a, b) => cardTitleOf(a).localeCompare(cardTitleOf(b)))
      .map((card) => ({
        ...card,
        id: cardIdOf(card),
        title: cardTitleOf(card),
        subtitle: cardSubtitleOf(card),
        description: stripHtml(card.description),
        image: cardImageOf(card),
        discovered: discovered.cards.includes(cardIdOf(card)),
      }));
  }, [cards, discovered.cards, enemyCardIds]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (selectedEntry) {
          setSelectedEntry(null);
          return;
        }
        onClose();
        return;
      }

      if (selectedEntry?.kind === "enemy" && unlockedEnemyEntries.length > 1) {
        const currentIndex = unlockedEnemyEntries.findIndex(
          (entry) => entry.id === selectedEntry.entry.id,
        );
        if (currentIndex >= 0 && event.key === "ArrowLeft") {
          const nextIndex =
            (currentIndex - 1 + unlockedEnemyEntries.length) %
            unlockedEnemyEntries.length;
          setSelectedEntry({
            kind: "enemy",
            entry: unlockedEnemyEntries[nextIndex],
          });
          return;
        }
        if (currentIndex >= 0 && event.key === "ArrowRight") {
          const nextIndex = (currentIndex + 1) % unlockedEnemyEntries.length;
          setSelectedEntry({
            kind: "enemy",
            entry: unlockedEnemyEntries[nextIndex],
          });
          return;
        }
      }

      if (selectedEntry?.kind === "relic" && unlockedRelicEntries.length > 1) {
        const currentIndex = unlockedRelicEntries.findIndex(
          (entry) => entry.id === selectedEntry.entry.id,
        );
        if (currentIndex >= 0 && event.key === "ArrowLeft") {
          const nextIndex =
            (currentIndex - 1 + unlockedRelicEntries.length) %
            unlockedRelicEntries.length;
          setSelectedEntry({
            kind: "relic",
            entry: unlockedRelicEntries[nextIndex],
          });
          return;
        }
        if (currentIndex >= 0 && event.key === "ArrowRight") {
          const nextIndex = (currentIndex + 1) % unlockedRelicEntries.length;
          setSelectedEntry({
            kind: "relic",
            entry: unlockedRelicEntries[nextIndex],
          });
          return;
        }
      }

      if (selectedEntry?.kind === "tarot" && unlockedTarotEntries.length > 1) {
        const currentIndex = unlockedTarotEntries.findIndex(
          (entry) => entry.id === selectedEntry.entry.id,
        );
        if (currentIndex >= 0 && event.key === "ArrowLeft") {
          const nextIndex =
            (currentIndex - 1 + unlockedTarotEntries.length) %
            unlockedTarotEntries.length;
          setSelectedEntry({
            kind: "tarot",
            entry: unlockedTarotEntries[nextIndex],
          });
          return;
        }
        if (currentIndex >= 0 && event.key === "ArrowRight") {
          const nextIndex = (currentIndex + 1) % unlockedTarotEntries.length;
          setSelectedEntry({
            kind: "tarot",
            entry: unlockedTarotEntries[nextIndex],
          });
          return;
        }
      }

      if (selectedEntry?.kind === "lore") {
        const lorePages = Array.isArray(selectedEntry.entry.pages)
          ? selectedEntry.entry.pages
          : [];
        if (lorePages.length > 1 && event.key === "ArrowLeft") {
          setSelectedLorePageIndex(
            (current) => (current - 1 + lorePages.length) % lorePages.length,
          );
          return;
        }
        if (lorePages.length > 1 && event.key === "ArrowRight") {
          setSelectedLorePageIndex((current) => (current + 1) % lorePages.length);
          return;
        }
      }

      if (selectedEntry?.kind === "card") {
        const unlockedCardEntries = cardEntries.filter(
          (entry) => entry.discovered,
        );
        if (unlockedCardEntries.length > 1) {
          const currentIndex = unlockedCardEntries.findIndex(
            (entry) => entry.id === selectedEntry.entry.id,
          );
          if (currentIndex >= 0 && event.key === "ArrowLeft") {
            const nextIndex =
              (currentIndex - 1 + unlockedCardEntries.length) %
              unlockedCardEntries.length;
            setSelectedEntry({
              kind: "card",
              entry: unlockedCardEntries[nextIndex],
            });
            return;
          }
          if (currentIndex >= 0 && event.key === "ArrowRight") {
            const nextIndex = (currentIndex + 1) % unlockedCardEntries.length;
            setSelectedEntry({
              kind: "card",
              entry: unlockedCardEntries[nextIndex],
            });
            return;
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    cardEntries,
    onClose,
    selectedEntry,
    unlockedEnemyEntries,
    unlockedRelicEntries,
    unlockedTarotEntries,
  ]);

  useEffect(() => {
    if (selectedEntry?.kind === "lore") {
      const maxIndex = Math.max(0, selectedEntry.entry.pages.length - 1);
      setSelectedLorePageIndex((current) =>
        Math.max(0, Math.min(current, maxIndex)),
      );
      return;
    }

    setSelectedLorePageIndex(0);
  }, [selectedEntry]);


  const compactLegendariumGrid =
    viewportSize.width <= 1700 || viewportSize.height <= 980;
  const tightLegendariumGrid =
    viewportSize.width <= 1380 || viewportSize.height <= 860;

  const tabCounters: Record<TabKey, string> = {
    enemies: `${enemyEntries.filter((entry) => entry.discovered).length}/${enemyEntries.length}`,
    relics: `${relicEntries.filter((entry) => entry.discovered).length}/${relicEntries.length}`,
    cards: `${cardEntries.filter((entry) => entry.discovered).length}/${cardEntries.length}`,
    tarot: `${tarotEntries.filter((entry) => entry.discovered).length}/${tarotEntries.length}`,
    lore: `${unlockedLoreEntries.length}/${loreEntries.length}`,
  };

  const tabs: Array<{ key: TabKey; label: string }> = [
    { key: "enemies", label: "Enemies" },
    { key: "relics", label: "Relics" },
    { key: "cards", label: "Cards" },
    { key: "tarot", label: "Tarot" },
    { key: "lore", label: "Lore" },
  ];

  const selectedTabWidth =
    tabs.length >= 5
      ? "clamp(168px, 14.8vw, 240px)"
      : "clamp(177px, 15.9vw, 251px)";
  const idleTabWidth =
    tabs.length >= 5
      ? "clamp(142px, 12.2vw, 208px)"
      : "clamp(150px, 13vw, 220px)";

  const renderTabButton = (tab: { key: TabKey; label: string }) => {
    const selected = tab.key === activeTab;

    return (
      <div
        key={tab.key}
        style={{
          position: "relative",
          height: "clamp(58px, 5.2vw, 82px)",
          width: selected ? selectedTabWidth : idleTabWidth,
          aspectRatio: selected ? LEGENDARIUM_TAB_ASPECT : undefined,
          flex: "0 0 auto",
          zIndex: selected ? 2 : 1,
        }}
      >
        <button
          type="button"
          onClick={() => {
            setActiveTab(tab.key);
            setSelectedEntry(null);
          }}
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            padding: 0,
            border: "none",
            background: selected ? "#000" : "transparent",
            cursor: "pointer",
            color: "#f2eee4",
            fontFamily: FONT_FAMILY,
            fontSize: selected
              ? "clamp(16px, 1.18vw, 22px)"
              : "clamp(14px, 1.04vw, 18px)",
            fontWeight: selected ? 700 : 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
          }}
        >
          {selected && (
            <>
              <img
                src={tabSrc}
                alt=""
                aria-hidden
                draggable={false}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  transform: "translateY(8px)",
                  pointerEvents: "none",
                  userSelect: "none",
                }}
              />
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  left: 0,
                  bottom: 0,
                  width: "11%",
                  height: 4,
                  background: "#000",
                  pointerEvents: "none",
                }}
              />
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  right: 0,
                  bottom: 0,
                  width: "11%",
                  height: 4,
                  background: "#000",
                  pointerEvents: "none",
                }}
              />
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  left: 0,
                  bottom: 3,
                  width: "1%",
                  height: 1,
                  background: "rgba(255,255,255,0.92)",
                  pointerEvents: "none",
                }}
              />
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  right: 0,
                  bottom: 3,
                  width: "1%",
                  height: 1,
                  background: "rgba(255,255,255,0.92)",
                  pointerEvents: "none",
                }}
              />
            </>
          )}
          <span
            style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: selected ? 4 : 0,
              lineHeight: 1,
              textShadow: "0 2px 8px rgba(0,0,0,0.9)",
              transform: selected ? "translateY(9px)" : "none",
              paddingInline: selected ? 26 : 10,
            }}
          >
            <span>{tab.label}</span>
            {selected && (
              <span
                style={{
                  fontSize: "clamp(12px, 0.95vw, 18px)",
                  fontWeight: 700,
                }}
              >
                {tabCounters[tab.key]}
              </span>
            )}
          </span>
        </button>
      </div>
    );
  };

  const undiscoveredFilter = "brightness(0.22) saturate(0.2) opacity(0.72)";

  const renderEnemyGrid = () => (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: tightLegendariumGrid
            ? "repeat(auto-fill, minmax(170px, 1fr))"
            : compactLegendariumGrid
              ? "repeat(auto-fill, minmax(188px, 1fr))"
              : "repeat(auto-fill, minmax(205px, 1fr))",
          gap: tightLegendariumGrid ? 16 : compactLegendariumGrid ? 20 : 22,
        }}
      >
        {enemyEntries.map((enemy) => {
          const hovered = hoveredEnemyCard === enemy.id;
          const showHoverOverlay = enemy.discovered && hovered;

          return (
            <button
              key={enemy.id}
              type="button"
              onClick={() =>
                enemy.discovered &&
                setSelectedEntry({ kind: "enemy", entry: enemy })
              }
              onMouseEnter={() =>
                enemy.discovered && setHoveredEnemyCard(enemy.id)
              }
              onMouseLeave={() =>
                setHoveredEnemyCard((current) =>
                  current === enemy.id ? null : current,
                )
              }
              onFocus={() => enemy.discovered && setHoveredEnemyCard(enemy.id)}
              onBlur={() =>
                setHoveredEnemyCard((current) =>
                  current === enemy.id ? null : current,
                )
              }
              style={{
                padding: 0,
                border: "none",
                background: "transparent",
                cursor: enemy.discovered ? "pointer" : "default",
                textAlign: "left",
              }}
            >
              <div
                style={{
                  position: "relative",
                  borderRadius: tightLegendariumGrid
                    ? 12
                    : compactLegendariumGrid
                      ? 14
                      : 16,
                  overflow: "hidden",
                  background: "#050505",
                  boxShadow: enemy.discovered
                    ? tightLegendariumGrid
                      ? "0 9px 24px rgba(0,0,0,0.48), 0 0 0 1px rgba(255,215,120,0.2)"
                      : compactLegendariumGrid
                        ? "0 10px 28px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,215,120,0.21)"
                        : "0 11px 30px rgba(0,0,0,0.52), 0 0 0 1px rgba(255,215,120,0.22)"
                    : tightLegendariumGrid
                      ? "0 8px 20px rgba(0,0,0,0.38)"
                      : compactLegendariumGrid
                        ? "0 9px 22px rgba(0,0,0,0.4)"
                        : "0 10px 24px rgba(0,0,0,0.42)",
                }}
              >
                <img
                  src={enemy.image}
                  alt={enemy.name}
                  draggable={false}
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    filter: enemy.discovered ? "none" : undiscoveredFilter,
                    userSelect: "none",
                  }}
                />

                {!enemy.discovered && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.34) 100%)",
                      pointerEvents: "none",
                    }}
                  />
                )}

                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "54%",
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.84) 100%)",
                    opacity: showHoverOverlay ? 1 : 0,
                    transition: "opacity 180ms ease",
                    pointerEvents: "none",
                  }}
                />

                <div
                  style={{
                    position: "absolute",
                    left: "8%",
                    right: "8%",
                    bottom: "6.4%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 3,
                    opacity: showHoverOverlay ? 1 : 0,
                    transform: showHoverOverlay
                      ? "translateY(0)"
                      : "translateY(8px)",
                    transition: "opacity 180ms ease, transform 180ms ease",
                    pointerEvents: "none",
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      width: tightLegendariumGrid
                        ? "68%"
                        : compactLegendariumGrid
                          ? "70%"
                          : "72%",
                      minWidth: tightLegendariumGrid
                        ? 108
                        : compactLegendariumGrid
                          ? 116
                          : 124,
                      maxWidth: tightLegendariumGrid
                        ? 176
                        : compactLegendariumGrid
                          ? 188
                          : 200,
                      marginBottom: -3,
                    }}
                  >
                    <img
                      src={cardUiTopSrc}
                      alt=""
                      aria-hidden
                      draggable={false}
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                        userSelect: "none",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#f4f0e8",
                        fontFamily: FONT_FAMILY,
                        fontWeight: 700,
                        fontSize: tightLegendariumGrid
                          ? "clamp(15px, 1.1vw, 22px)"
                          : compactLegendariumGrid
                            ? "clamp(16px, 1.2vw, 24px)"
                            : "clamp(17px, 1.3vw, 26px)",
                        lineHeight: 1,
                        textShadow: "0 2px 8px rgba(0,0,0,0.95)",
                        transform: "translateY(-6%)",
                      }}
                    >
                      {enemy.numeral}
                    </div>
                  </div>

                  <div
                    style={{
                      width: tightLegendariumGrid ? "88%" : "86%",
                      color: "#f4f0e8",
                      fontFamily: FONT_FAMILY,
                      fontWeight: 700,
                      fontSize: tightLegendariumGrid
                        ? "clamp(15px, 1vw, 19px)"
                        : compactLegendariumGrid
                          ? "clamp(16px, 1.08vw, 20px)"
                          : "clamp(17px, 1.16vw, 22px)",
                      lineHeight: 1.08,
                      textAlign: "center",
                      textShadow: "0 2px 8px rgba(0,0,0,0.95)",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 2,
                      overflow: "hidden",
                      padding: "0.14em 0.22em 0.2em",
                    }}
                    title={enemy.name}
                  >
                    {enemy.name}
                  </div>

                  <img
                    src={cardUiBottomSrc}
                    alt=""
                    aria-hidden
                    draggable={false}
                    style={{
                      width: tightLegendariumGrid
                        ? "68%"
                        : compactLegendariumGrid
                          ? "70%"
                          : "72%",
                      minWidth: tightLegendariumGrid
                        ? 108
                        : compactLegendariumGrid
                          ? 116
                          : 124,
                      maxWidth: tightLegendariumGrid
                        ? 176
                        : compactLegendariumGrid
                          ? 188
                          : 200,
                      height: "auto",
                      display: "block",
                      userSelect: "none",
                      marginTop: -1,
                      transform: tightLegendariumGrid
                        ? "translateY(-6px)"
                        : "translateY(-8px)",
                    }}
                  />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </>
  );

  const renderRelicGrid = () => (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: tightLegendariumGrid
            ? "repeat(auto-fill, minmax(150px, 1fr))"
            : compactLegendariumGrid
              ? "repeat(auto-fill, minmax(168px, 1fr))"
              : "repeat(auto-fill, minmax(182px, 1fr))",
          gap: tightLegendariumGrid ? 12 : compactLegendariumGrid ? 14 : 16,
        }}
      >
        {relicEntries.map((relic) => (
          <button
            key={relic.id}
            type="button"
            onClick={() =>
              relic.discovered &&
              setSelectedEntry({ kind: "relic", entry: relic })
            }
            style={{
              border: "1px solid rgba(255,255,255,0.08)",
              background: "rgba(0,0,0,0.74)",
              borderRadius: tightLegendariumGrid
                ? 14
                : compactLegendariumGrid
                  ? 15
                  : 16,
              padding: tightLegendariumGrid
                ? "12px 10px 11px"
                : compactLegendariumGrid
                  ? "14px 12px 12px"
                  : "15px 13px 13px",
              cursor: relic.discovered ? "pointer" : "default",
              textAlign: "left",
              minHeight: tightLegendariumGrid
                ? 226
                : compactLegendariumGrid
                  ? 248
                  : 264,
              boxShadow: tightLegendariumGrid
                ? "0 8px 20px rgba(0,0,0,0.28)"
                : compactLegendariumGrid
                  ? "0 9px 22px rgba(0,0,0,0.3)"
                  : "0 10px 24px rgba(0,0,0,0.31)",
            }}
          >
            <div
              style={{
                width: "100%",
                aspectRatio: "1 / 1",
                borderRadius: tightLegendariumGrid
                  ? 10
                  : compactLegendariumGrid
                    ? 11
                    : 12,
                background: "#000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: tightLegendariumGrid
                  ? 8
                  : compactLegendariumGrid
                    ? 9
                    : 10,
                overflow: "hidden",
              }}
            >
              <img
                src={relic.image}
                alt={relic.name}
                draggable={false}
                style={{
                  width: tightLegendariumGrid
                    ? "72%"
                    : compactLegendariumGrid
                      ? "74%"
                      : "76%",
                  height: tightLegendariumGrid
                    ? "72%"
                    : compactLegendariumGrid
                      ? "74%"
                      : "76%",
                  objectFit: "contain",
                  filter: relic.discovered ? "none" : undiscoveredFilter,
                  userSelect: "none",
                }}
              />
            </div>
            <div
              style={{
                color: relic.discovered ? "#f6f1e7" : "rgba(255,255,255,0.4)",
                fontFamily: FONT_FAMILY,
                fontWeight: 700,
                fontSize: tightLegendariumGrid
                  ? 15
                  : compactLegendariumGrid
                    ? 16
                    : 17,
                lineHeight: 1,
                height: tightLegendariumGrid
                  ? 30
                  : compactLegendariumGrid
                    ? 32
                    : 34,
                marginBottom: 0,
                whiteSpace: "normal",
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
                overflow: "hidden",
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              {relic.name}
            </div>
            <div
              style={{
                color: relic.discovered
                  ? "rgba(240,240,240,0.82)"
                  : "transparent",
                fontFamily: FONT_FAMILY,
                fontWeight: 400,
                fontSize: tightLegendariumGrid
                  ? 10.5
                  : compactLegendariumGrid
                    ? 11
                    : 11.5,
                lineHeight: "1.18em",
                height: tightLegendariumGrid
                  ? "3.2em"
                  : compactLegendariumGrid
                    ? "3.3em"
                    : "3.36em",
                overflow: "hidden",
                whiteSpace: "normal",
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 3,
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
              title={
                relic.discovered
                  ? String(relic.description || "")
                  : "Undiscovered"
              }
            >
              {relic.discovered ? relic.description : "Undiscovered"}
            </div>
          </button>
        ))}
      </div>
    </>
  );

  // Virtualized card tile renderer. Renders a single card in a full-size cell with a consistent aspect ratio.
  const renderVirtualizedCardTile = useCallback(
    (card: (typeof cardEntries)[number]) => (
      <button
        type="button"
        onClick={() =>
          card.discovered &&
          setSelectedEntry({ kind: "card", entry: card })
        }
        style={{
          width: "100%",
          height: "100%",
          padding: 0,
          border: "none",
          background: "transparent",
          cursor: card.discovered ? "pointer" : "default",
          textAlign: "left",
          display: "block",
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            borderRadius: 12,
            overflow: "hidden",
            background: "transparent",
            boxShadow: "0 12px 32px rgba(0,0,0,0.42)",
            lineHeight: 0,
          }}
        >
          {card.image ? (
            <img
              src={card.image}
              alt={card.title}
              draggable={false}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                background: "transparent",
                userSelect: "none",
                filter: card.discovered ? "none" : undiscoveredFilter,
              }}
            />
          ) : (
            <div
              style={{
                aspectRatio: COMBAT_CARD_ASPECT,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  "linear-gradient(180deg, rgba(45,45,48,1) 0%, rgba(11,11,14,1) 100%)",
                color: card.discovered
                  ? "#f2eee4"
                  : "rgba(255,255,255,0.3)",
                fontFamily: FONT_FAMILY,
                fontWeight: 700,
                fontSize: 24,
                padding: 20,
                textAlign: "center",
              }}
            >
              {card.title}
            </div>
          )}
        </div>
      </button>
    ),
    [setSelectedEntry, undiscoveredFilter],
  );

  // Virtualized lore tile renderer. Renders a single lore entry card with hover and discovered state handling.
  const renderVirtualizedLoreTile = useCallback(
    (entry: LoreEntryRecord) => {
      const hovered = hoveredLoreCard === entry.id;
      const showHoverOverlay = entry.discovered && hovered;
      return (
        <button
          type="button"
          onClick={() => entry.discovered && openLoreEntry(entry)}
          onMouseEnter={() => entry.discovered && setHoveredLoreCard(entry.id)}
          onMouseLeave={() =>
            setHoveredLoreCard((current) =>
              current === entry.id ? null : current,
            )
          }
          onFocus={() => entry.discovered && setHoveredLoreCard(entry.id)}
          onBlur={() =>
            setHoveredLoreCard((current) =>
              current === entry.id ? null : current,
            )
          }
          style={{
            width: "100%",
            height: "100%",
            padding: 0,
            border: "none",
            background: "transparent",
            cursor: entry.discovered ? "pointer" : "default",
            textAlign: "left",
            display: "block",
          }}
        >
          <div
            style={{
              position: "relative",
              aspectRatio: tightLegendariumGrid ? "0.76 / 1" : "0.8 / 1",
              borderRadius: tightLegendariumGrid
                ? 18
                : compactLegendariumGrid
                ? 20
                : 22,
              overflow: "hidden",
              background: "#050505",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: entry.discovered
                ? tightLegendariumGrid
                  ? "0 10px 24px rgba(0,0,0,0.48), 0 0 0 1px rgba(255,255,255,0.08)"
                  : compactLegendariumGrid
                  ? "0 12px 28px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1)"
                  : "0 14px 32px rgba(0,0,0,0.52), 0 0 0 1px rgba(255,255,255,0.12)"
                : "0 10px 26px rgba(0,0,0,0.44)",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.2) 38%, rgba(0,0,0,0.82) 100%)",
                zIndex: 1,
                pointerEvents: "none",
              }}
            />

            {entry.image ? (
              <img
                src={entry.image}
                alt={entry.title}
                draggable={false}
                style={{
                  position: "absolute",
                  inset: "6.5% 12% 8.5% 12%",
                  width: "76%",
                  height: "85%",
                  objectFit: "cover",
                  filter: entry.discovered ? "none" : undiscoveredFilter,
                  userSelect: "none",
                }}
              />
            ) : (
              <div
                style={{
                  position: "absolute",
                  inset: "6.5% 12% 8.5% 12%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: entry.discovered
                    ? "#f2eee4"
                    : "rgba(255,255,255,0.35)",
                  fontFamily: FONT_FAMILY,
                  fontWeight: 700,
                  fontSize: tightLegendariumGrid ? 20 : 24,
                  textAlign: "center",
                  padding: 16,
                  background:
                    "linear-gradient(180deg, rgba(68,48,20,0.72) 0%, rgba(27,18,8,0.92) 100%)",
                }}
              >
                {entry.title}
              </div>
            )}

            {entry.showPartTracker ? (
              <div
                style={{
                  position: "absolute",
                  top: tightLegendariumGrid ? "8%" : "7%",
                  left: tightLegendariumGrid ? "30%" : "28%",
                  display: "flex",
                  alignItems: "center",
                  gap: tightLegendariumGrid ? 4 : 6,
                  zIndex: 3,
                  pointerEvents: "none",
                }}
              >
                {Array.from({ length: entry.trackerCount }).map((_, index) => (
                  <img
                    key={`${entry.id}:part:${index}`}
                    src={
                      index < entry.discoveredCount
                        ? loreEntryFullSrc
                        : loreEntryBaseSrc
                    }
                    alt=""
                    aria-hidden
                    draggable={false}
                    style={{
                      width: tightLegendariumGrid
                        ? 22
                        : compactLegendariumGrid
                        ? 24
                        : 26,
                      height: tightLegendariumGrid
                        ? 22
                        : compactLegendariumGrid
                        ? 24
                        : 26,
                      objectFit: "contain",
                      opacity: index < entry.discoveredCount ? 1 : 0.64,
                      filter:
                        index < entry.discoveredCount
                          ? "drop-shadow(0 2px 6px rgba(0,0,0,0.6))"
                          : "drop-shadow(0 2px 4px rgba(0,0,0,0.55))",
                      userSelect: "none",
                    }}
                  />
                ))}
              </div>
            ) : !!entry.badgeText ? (
              <div
                style={{
                  position: "absolute",
                  top: tightLegendariumGrid ? "8%" : "7%",
                  left: tightLegendariumGrid ? "8%" : "7%",
                  minWidth: tightLegendariumGrid ? 48 : 54,
                  padding: tightLegendariumGrid
                    ? "6px 10px 4px"
                    : "7px 12px 5px",
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.65)",
                  background: "rgba(0,0,0,0.62)",
                  color: "#f4f0e8",
                  fontFamily: FONT_FAMILY,
                  fontWeight: 700,
                  fontSize: tightLegendariumGrid ? 13 : 14,
                  lineHeight: 1,
                  textAlign: "center",
                  textShadow: "0 2px 8px rgba(0,0,0,0.9)",
                  zIndex: 3,
                  pointerEvents: "none",
                }}
              >
                {entry.badgeText}
              </div>
            ) : null}

            {!entry.discovered && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  padding: tightLegendariumGrid
                    ? "0 14px 16px"
                    : "0 16px 18px",
                  color: "rgba(255,255,255,0.5)",
                  fontFamily: FONT_FAMILY,
                  fontWeight: 700,
                  fontSize: tightLegendariumGrid ? 17 : 19,
                  textAlign: "center",
                  textShadow: "0 2px 8px rgba(0,0,0,0.92)",
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              >
                Undiscovered
              </div>
            )}

            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: "58%",
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.84) 100%)",
                opacity: showHoverOverlay ? 1 : 0,
                transition: "opacity 180ms ease",
                pointerEvents: "none",
                zIndex: 2,
              }}
            />

            <div
              style={{
                position: "absolute",
                left: "8%",
                right: "8%",
                bottom: "7%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                opacity: showHoverOverlay ? 1 : 0,
                transform: showHoverOverlay
                  ? "translateY(0)"
                  : "translateY(8px)",
                transition: "opacity 180ms ease, transform 180ms ease",
                pointerEvents: "none",
                zIndex: 3,
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: tightLegendariumGrid
                    ? "68%"
                    : compactLegendariumGrid
                    ? "70%"
                    : "72%",
                  minWidth: tightLegendariumGrid
                    ? 108
                    : compactLegendariumGrid
                    ? 116
                    : 124,
                  maxWidth: tightLegendariumGrid
                    ? 176
                    : compactLegendariumGrid
                    ? 188
                    : 200,
                  marginBottom: -3,
                }}
              >
                <img
                  src={cardUiTopSrc}
                  alt=""
                  aria-hidden
                  draggable={false}
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    userSelect: "none",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#f4f0e8",
                    fontFamily: FONT_FAMILY,
                    fontWeight: 700,
                    fontSize: tightLegendariumGrid
                      ? "clamp(15px, 1.1vw, 22px)"
                      : compactLegendariumGrid
                      ? "clamp(16px, 1.2vw, 24px)"
                      : "clamp(17px, 1.3vw, 26px)",
                    lineHeight: 1,
                    textShadow: "0 2px 8px rgba(0,0,0,0.95)",
                    transform: "translateY(-6%)",
                  }}
                >
                  {entry.numeral}
                </div>
              </div>

              <div
                style={{
                  width: tightLegendariumGrid ? "88%" : "86%",
                  color: "#f4f0e8",
                  fontFamily: FONT_FAMILY,
                  fontWeight: 700,
                  fontSize: tightLegendariumGrid
                    ? "clamp(15px, 1vw, 19px)"
                    : compactLegendariumGrid
                    ? "clamp(16px, 1.08vw, 20px)"
                    : "clamp(17px, 1.16vw, 22px)",
                  lineHeight: 1.08,
                  textAlign: "center",
                  textShadow: "0 2px 8px rgba(0,0,0,0.95)",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 3,
                  overflow: "hidden",
                  padding: "0.14em 0.22em 0.2em",
                }}
                title={entry.title}
              >
                {entry.title}
              </div>

              <img
                src={cardUiBottomSrc}
                alt=""
                aria-hidden
                draggable={false}
                style={{
                  width: tightLegendariumGrid
                    ? "68%"
                    : compactLegendariumGrid
                    ? "70%"
                    : "72%",
                  minWidth: tightLegendariumGrid
                    ? 108
                    : compactLegendariumGrid
                    ? 116
                    : 124,
                  maxWidth: tightLegendariumGrid
                    ? 176
                    : compactLegendariumGrid
                    ? 188
                    : 200,
                  height: "auto",
                  display: "block",
                  userSelect: "none",
                  marginTop: -1,
                  transform: tightLegendariumGrid
                    ? "translateY(-6px)"
                    : "translateY(-8px)",
                }}
              />
            </div>
          </div>
        </button>
      );
    },
    [
      hoveredLoreCard,
      openLoreEntry,
      setHoveredLoreCard,
      tightLegendariumGrid,
      compactLegendariumGrid,
      undiscoveredFilter,
    ],
  );

  const renderTarotGrid = () => (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: tightLegendariumGrid ? 18 : compactLegendariumGrid ? 20 : 24,
        }}
      >
        {tarotEntries.map((tarot) => (
          <button
            key={tarot.id}
            type="button"
            onClick={() =>
              tarot.discovered &&
              setSelectedEntry({ kind: "tarot", entry: tarot })
            }
            aria-label={tarot.discovered ? tarot.name : "Undiscovered tarot card"}
            title={tarot.discovered ? tarot.name : "Undiscovered"}
            style={{
              border: "1px solid rgba(255,255,255,0.08)",
              background: "rgba(0,0,0,0.76)",
              borderRadius: tightLegendariumGrid
                ? 8
                : compactLegendariumGrid
                  ? 9
                  : 10,
              padding: 0,
              cursor: tarot.discovered ? "pointer" : "default",
              textAlign: "left",
              width: "100%",
              aspectRatio: TAROT_CARD_ASPECT,
              overflow: "hidden",
              display: "block",
              boxShadow: tightLegendariumGrid
                ? "0 8px 18px rgba(0,0,0,0.26)"
                : compactLegendariumGrid
                  ? "0 9px 20px rgba(0,0,0,0.28)"
                  : "0 10px 22px rgba(0,0,0,0.3)",
            }}
          >
            <img
              src={tarot.image}
              alt={tarot.name}
              draggable={false}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: "block",
                filter: tarot.discovered ? "none" : undiscoveredFilter,
                userSelect: "none",
              }}
            />
          </button>
        ))}
      </div>
    </>
  );

  const renderCardsGrid = () => {
    if (cardsLoading) {
      return (
        <div
          style={{
            color: "#f2eee4",
            fontFamily: FONT_FAMILY,
            fontSize: 24,
            paddingTop: 24,
          }}
        >
          Loading combat cards…
        </div>
      );
    }

    if (cardsError) {
      return (
        <div
          style={{
            color: "#f2eee4",
            fontFamily: FONT_FAMILY,
            fontSize: 24,
            paddingTop: 24,
          }}
        >
          {cardsError}
        </div>
      );
    }

    if (!cardEntries.length) {
      return (
        <div
          style={{
            color: "#f2eee4",
            fontFamily: FONT_FAMILY,
            fontSize: 24,
            paddingTop: 24,
          }}
        >
          No combat cards are available yet. The Legendarium will refresh automatically when they load.
        </div>
      );
    }

    return (
      <VirtualizedGrid
        items={cardEntries}
        scrollContainerRef={contentScrollRef}
        enabled={activeTab === "cards"}
        minColumnWidth={220}
        columnGap={tightLegendariumGrid ? 18 : compactLegendariumGrid ? 20 : 24}
        rowGap={tightLegendariumGrid ? 58 : compactLegendariumGrid ? 60 : 64}
        getItemKey={(card) => card.id}
        getItemHeight={(columnWidth) => (columnWidth * 420) / 288}
        renderItem={(card) => renderVirtualizedCardTile(card)}
      />
    );
  };

  function openLoreEntry(entry: LoreEntryRecord) {
    setSelectedLorePageIndex(getInitialLorePageIndex(entry));
    setSelectedEntry({ kind: "lore", entry });
  }

  const renderLoreGrid = () => {
    if (!loreEntries.length) {
      return (
        <div
          style={{
            color: "#f2eee4",
            fontFamily: FONT_FAMILY,
            fontSize: 24,
            paddingTop: 24,
          }}
        >
          No lore entries have been catalogued yet.
        </div>
      );
    }

    // Virtualize the lore entries grid. When entries exist, render via VirtualizedGrid and
    // immediately return. The fallback grid rendering code below remains for reference but
    // will not execute because of this early return.
    return (
      <VirtualizedGrid
        items={loreEntries}
        scrollContainerRef={contentScrollRef}
        enabled={activeTab === "lore"}
        minColumnWidth={
          tightLegendariumGrid ? 178 : compactLegendariumGrid ? 196 : 214
        }
        columnGap={
          tightLegendariumGrid ? 12 : compactLegendariumGrid ? 14 : 16
        }
        rowGap={
          tightLegendariumGrid ? 12 : compactLegendariumGrid ? 14 : 16
        }
        getItemKey={(entry) => entry.id}
        getItemHeight={(columnWidth) =>
          tightLegendariumGrid ? columnWidth / 0.76 : columnWidth / 0.8
        }
        renderItem={(entry) => renderVirtualizedLoreTile(entry)}
      />
    );

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: tightLegendariumGrid
            ? "repeat(auto-fill, minmax(178px, 1fr))"
            : compactLegendariumGrid
              ? "repeat(auto-fill, minmax(196px, 1fr))"
              : "repeat(auto-fill, minmax(214px, 1fr))",
          gap: tightLegendariumGrid ? 12 : compactLegendariumGrid ? 14 : 16,
        }}
      >
        {loreEntries.map((entry) => {
          const hovered = hoveredLoreCard === entry.id;
          const showHoverOverlay = entry.discovered && hovered;

          return (
            <button
              key={entry.id}
              type="button"
              onClick={() => entry.discovered && openLoreEntry(entry)}
              onMouseEnter={() => entry.discovered && setHoveredLoreCard(entry.id)}
              onMouseLeave={() =>
                setHoveredLoreCard((current) => (current === entry.id ? null : current))
              }
              onFocus={() => entry.discovered && setHoveredLoreCard(entry.id)}
              onBlur={() =>
                setHoveredLoreCard((current) => (current === entry.id ? null : current))
              }
              style={{
                padding: 0,
                border: "none",
                background: "transparent",
                cursor: entry.discovered ? "pointer" : "default",
                textAlign: "left",
              }}
            >
              <div
                style={{
                  position: "relative",
                  aspectRatio: tightLegendariumGrid ? "0.76 / 1" : "0.8 / 1",
                  borderRadius: tightLegendariumGrid ? 18 : compactLegendariumGrid ? 20 : 22,
                  overflow: "hidden",
                  background: "#050505",
                  border: "1px solid rgba(255,255,255,0.08)",
                  boxShadow: entry.discovered
                    ? tightLegendariumGrid
                      ? "0 10px 24px rgba(0,0,0,0.48), 0 0 0 1px rgba(255,255,255,0.08)"
                      : compactLegendariumGrid
                        ? "0 12px 28px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1)"
                        : "0 14px 32px rgba(0,0,0,0.52), 0 0 0 1px rgba(255,255,255,0.12)"
                    : "0 10px 26px rgba(0,0,0,0.44)",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.2) 38%, rgba(0,0,0,0.82) 100%)",
                    zIndex: 1,
                    pointerEvents: "none",
                  }}
                />

                {entry.image ? (
                  <img
                    src={entry.image}
                    alt={entry.title}
                    draggable={false}
                    style={{
                      position: "absolute",
                      inset: "6.5% 12% 8.5% 12%",
                      width: "76%",
                      height: "85%",
                      objectFit: "cover",
                      filter: entry.discovered ? "none" : undiscoveredFilter,
                      userSelect: "none",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      position: "absolute",
                      inset: "6.5% 12% 8.5% 12%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: entry.discovered ? "#f2eee4" : "rgba(255,255,255,0.35)",
                      fontFamily: FONT_FAMILY,
                      fontWeight: 700,
                      fontSize: tightLegendariumGrid ? 20 : 24,
                      textAlign: "center",
                      padding: 16,
                      background:
                        "linear-gradient(180deg, rgba(68,48,20,0.72) 0%, rgba(27,18,8,0.92) 100%)",
                    }}
                  >
                    {entry.title}
                  </div>
                )}

                {entry.showPartTracker ? (
                  <div
                    style={{
                      position: "absolute",
                      top: tightLegendariumGrid ? "8%" : "7%",
                      left: tightLegendariumGrid ? "30%" : "28%",
                      display: "flex",
                      alignItems: "center",
                      gap: tightLegendariumGrid ? 4 : 6,
                      zIndex: 3,
                      pointerEvents: "none",
                    }}
                  >
                    {Array.from({ length: entry.trackerCount }).map((_, index) => (
                      <img
                        key={`${entry.id}:part:${index}`}
                        src={index < entry.discoveredCount ? loreEntryFullSrc : loreEntryBaseSrc}
                        alt=""
                        aria-hidden
                        draggable={false}
                        style={{
                          width: tightLegendariumGrid ? 22 : compactLegendariumGrid ? 24 : 26,
                          height: tightLegendariumGrid ? 22 : compactLegendariumGrid ? 24 : 26,
                          objectFit: "contain",
                          opacity: index < entry.discoveredCount ? 1 : 0.64,
                          filter:
                            index < entry.discoveredCount
                              ? "drop-shadow(0 2px 6px rgba(0,0,0,0.6))"
                              : "drop-shadow(0 2px 4px rgba(0,0,0,0.55))",
                          userSelect: "none",
                        }}
                      />
                    ))}
                  </div>
                ) : !!entry.badgeText ? (
                  <div
                    style={{
                      position: "absolute",
                      top: tightLegendariumGrid ? "8%" : "7%",
                      left: tightLegendariumGrid ? "8%" : "7%",
                      minWidth: tightLegendariumGrid ? 48 : 54,
                      padding: tightLegendariumGrid ? "6px 10px 4px" : "7px 12px 5px",
                      borderRadius: 999,
                      border: "1px solid rgba(255,255,255,0.65)",
                      background: "rgba(0,0,0,0.62)",
                      color: "#f4f0e8",
                      fontFamily: FONT_FAMILY,
                      fontWeight: 700,
                      fontSize: tightLegendariumGrid ? 13 : 14,
                      lineHeight: 1,
                      textAlign: "center",
                      textShadow: "0 2px 8px rgba(0,0,0,0.9)",
                      zIndex: 3,
                      pointerEvents: "none",
                    }}
                  >
                    {entry.badgeText}
                  </div>
                ) : null}

                {!entry.discovered && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "center",
                      padding: tightLegendariumGrid ? "0 14px 16px" : "0 16px 18px",
                      color: "rgba(255,255,255,0.5)",
                      fontFamily: FONT_FAMILY,
                      fontWeight: 700,
                      fontSize: tightLegendariumGrid ? 17 : 19,
                      textAlign: "center",
                      textShadow: "0 2px 8px rgba(0,0,0,0.92)",
                      zIndex: 2,
                      pointerEvents: "none",
                    }}
                  >
                    Undiscovered
                  </div>
                )}

                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "58%",
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.84) 100%)",
                    opacity: showHoverOverlay ? 1 : 0,
                    transition: "opacity 180ms ease",
                    pointerEvents: "none",
                    zIndex: 2,
                  }}
                />

                <div
                  style={{
                    position: "absolute",
                    left: "8%",
                    right: "8%",
                    bottom: "7%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 3,
                    opacity: showHoverOverlay ? 1 : 0,
                    transform: showHoverOverlay ? "translateY(0)" : "translateY(8px)",
                    transition: "opacity 180ms ease, transform 180ms ease",
                    pointerEvents: "none",
                    zIndex: 3,
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      width: tightLegendariumGrid ? "68%" : compactLegendariumGrid ? "70%" : "72%",
                      minWidth: tightLegendariumGrid ? 108 : compactLegendariumGrid ? 116 : 124,
                      maxWidth: tightLegendariumGrid ? 176 : compactLegendariumGrid ? 188 : 200,
                      marginBottom: -3,
                    }}
                  >
                    <img
                      src={cardUiTopSrc}
                      alt=""
                      aria-hidden
                      draggable={false}
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                        userSelect: "none",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#f4f0e8",
                        fontFamily: FONT_FAMILY,
                        fontWeight: 700,
                        fontSize: tightLegendariumGrid
                          ? "clamp(15px, 1.1vw, 22px)"
                          : compactLegendariumGrid
                            ? "clamp(16px, 1.2vw, 24px)"
                            : "clamp(17px, 1.3vw, 26px)",
                        lineHeight: 1,
                        textShadow: "0 2px 8px rgba(0,0,0,0.95)",
                        transform: "translateY(-6%)",
                      }}
                    >
                      {entry.numeral}
                    </div>
                  </div>

                  <div
                    style={{
                      width: tightLegendariumGrid ? "88%" : "86%",
                      color: "#f4f0e8",
                      fontFamily: FONT_FAMILY,
                      fontWeight: 700,
                      fontSize: tightLegendariumGrid
                        ? "clamp(15px, 1vw, 19px)"
                        : compactLegendariumGrid
                          ? "clamp(16px, 1.08vw, 20px)"
                          : "clamp(17px, 1.16vw, 22px)",
                      lineHeight: 1.08,
                      textAlign: "center",
                      textShadow: "0 2px 8px rgba(0,0,0,0.95)",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 3,
                      overflow: "hidden",
                      padding: "0.14em 0.22em 0.2em",
                    }}
                    title={entry.title}
                  >
                    {entry.title}
                  </div>

                  <img
                    src={cardUiBottomSrc}
                    alt=""
                    aria-hidden
                    draggable={false}
                    style={{
                      width: tightLegendariumGrid ? "68%" : compactLegendariumGrid ? "70%" : "72%",
                      minWidth: tightLegendariumGrid ? 108 : compactLegendariumGrid ? 116 : 124,
                      maxWidth: tightLegendariumGrid ? 176 : compactLegendariumGrid ? 188 : 200,
                      height: "auto",
                      display: "block",
                      userSelect: "none",
                      marginTop: -1,
                      transform: tightLegendariumGrid ? "translateY(-6px)" : "translateY(-8px)",
                    }}
                  />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  const cycleSelectedEnemy = (direction: -1 | 1) => {
    if (
      !selectedEntry ||
      selectedEntry.kind !== "enemy" ||
      unlockedEnemyEntries.length <= 1
    )
      return;

    const currentIndex = unlockedEnemyEntries.findIndex(
      (entry) => entry.id === selectedEntry.entry.id,
    );
    if (currentIndex < 0) return;

    const nextIndex =
      (currentIndex + direction + unlockedEnemyEntries.length) %
      unlockedEnemyEntries.length;
    setSelectedEntry({ kind: "enemy", entry: unlockedEnemyEntries[nextIndex] });
  };

  const cycleSelectedRelic = (direction: -1 | 1) => {
    if (
      !selectedEntry ||
      selectedEntry.kind !== "relic" ||
      unlockedRelicEntries.length <= 1
    )
      return;

    const currentIndex = unlockedRelicEntries.findIndex(
      (entry) => entry.id === selectedEntry.entry.id,
    );
    if (currentIndex < 0) return;

    const nextIndex =
      (currentIndex + direction + unlockedRelicEntries.length) %
      unlockedRelicEntries.length;
    setSelectedEntry({ kind: "relic", entry: unlockedRelicEntries[nextIndex] });
  };

  const cycleSelectedTarot = (direction: -1 | 1) => {
    if (!selectedEntry || selectedEntry.kind !== "tarot") return;
    if (unlockedTarotEntries.length <= 1) return;

    const currentIndex = unlockedTarotEntries.findIndex(
      (entry) => entry.id === selectedEntry.entry.id,
    );
    if (currentIndex < 0) return;

    const nextIndex =
      (currentIndex + direction + unlockedTarotEntries.length) %
      unlockedTarotEntries.length;
    setSelectedEntry({ kind: "tarot", entry: unlockedTarotEntries[nextIndex] });
  };

  const cycleSelectedCard = (direction: -1 | 1) => {
    if (!selectedEntry || selectedEntry.kind !== "card") return;

    const unlockedCardEntries = cardEntries.filter((entry) => entry.discovered);
    if (unlockedCardEntries.length <= 1) return;

    const currentIndex = unlockedCardEntries.findIndex(
      (entry) => entry.id === selectedEntry.entry.id,
    );
    if (currentIndex < 0) return;

    const nextIndex =
      (currentIndex + direction + unlockedCardEntries.length) %
      unlockedCardEntries.length;
    setSelectedEntry({ kind: "card", entry: unlockedCardEntries[nextIndex] });
  };

  const cycleSelectedLorePage = (direction: -1 | 1) => {
    if (!selectedEntry || selectedEntry.kind !== "lore") return;

    const pages = Array.isArray(selectedEntry.entry.pages)
      ? selectedEntry.entry.pages
      : [];
    if (pages.length <= 1) return;

    setSelectedLorePageIndex(
      (current) => (current + direction + pages.length) % pages.length,
    );
  };

  const renderSelectedModal = () => {
    if (!selectedEntry) return null;

    const compactEnemyModal =
      viewportSize.width <= 1720 || viewportSize.height <= 980;
    const tightEnemyModal =
      viewportSize.width <= 1480 || viewportSize.height <= 900;
    const compactRelicModal =
      viewportSize.width <= 1600 || viewportSize.height <= 960;
    const tightRelicModal =
      viewportSize.width <= 1260 || viewportSize.height <= 840;
    const stackedRelicModal =
      viewportSize.width <= 980 || viewportSize.height <= 820;

    if (selectedEntry.kind === "enemy") {
      const enemy = selectedEntry.entry;
      const canCycleEnemies = unlockedEnemyEntries.length > 1;

      return (
        <div
          onClick={() => setSelectedEntry(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 60,
            background: "rgba(0,0,0,0.88)",
            overflowY: "auto",
            overflowX: "visible",
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              position: "relative",
              width: "100%",
              minHeight: "100vh",
              overflow: "visible",
              padding: tightEnemyModal
                ? "18px 74px 24px"
                : compactEnemyModal
                  ? "20px 92px 28px"
                  : "22px 112px 34px",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <button
              type="button"
              onClick={() => setSelectedEntry(null)}
              style={{
                position: "fixed",
                top: 22,
                right: 28,
                width: "min(4.2vw, 68px)",
                minWidth: 40,
                aspectRatio: "1 / 1",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: 0,
                zIndex: 5,
              }}
            >
              <img
                src={closeButtonSrc}
                alt="Close enemy modal"
                draggable={false}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </button>
            <button
              type="button"
              onClick={() => cycleSelectedEnemy(-1)}
              disabled={!canCycleEnemies}
              aria-label="Previous enemy"
              style={{
                position: "absolute",
                left: tightEnemyModal
                  ? "calc(50% - 420px)"
                  : compactEnemyModal
                    ? "calc(50% - 470px)"
                    : "calc(50% - 520px)",
                top: "50%",
                transform: "translate(-100%, -50%)",
                width: tightEnemyModal ? 42 : compactEnemyModal ? 50 : 58,
                height: tightEnemyModal ? 72 : compactEnemyModal ? 84 : 96,
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: canCycleEnemies ? "pointer" : "default",
                opacity: canCycleEnemies ? 0.96 : 0.28,
              }}
            >
              <img
                src={buttonArrowSrc}
                alt=""
                aria-hidden
                draggable={false}
                style={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                  objectFit: "contain",
                }}
              />
            </button>

            <button
              type="button"
              onClick={() => cycleSelectedEnemy(1)}
              disabled={!canCycleEnemies}
              aria-label="Next enemy"
              style={{
                position: "absolute",
                right: tightEnemyModal
                  ? "calc(50% - 420px)"
                  : compactEnemyModal
                    ? "calc(50% - 470px)"
                    : "calc(50% - 520px)",
                top: "50%",
                transform: "translate(100%, -50%)",
                width: tightEnemyModal ? 42 : compactEnemyModal ? 50 : 58,
                height: tightEnemyModal ? 72 : compactEnemyModal ? 84 : 96,
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: canCycleEnemies ? "pointer" : "default",
                opacity: canCycleEnemies ? 0.96 : 0.28,
              }}
            >
              <img
                src={buttonArrowSrc}
                alt=""
                aria-hidden
                draggable={false}
                style={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                  objectFit: "contain",
                  transform: "scaleX(-1)",
                }}
              />
            </button>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: tightEnemyModal
                  ? "minmax(300px, 340px) minmax(420px, 470px)"
                  : compactEnemyModal
                    ? "minmax(330px, 380px) minmax(450px, 510px)"
                    : "minmax(380px, 430px) minmax(520px, 560px)",
                gap: tightEnemyModal ? 18 : compactEnemyModal ? 22 : 28,
                alignItems: "center",
                justifyContent: "center",
                overflow: "visible",
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  paddingTop: tightEnemyModal ? 2 : compactEnemyModal ? 4 : 6,
                  paddingLeft: tightEnemyModal
                    ? 18
                    : compactEnemyModal
                      ? 24
                      : 34,
                  display: "flex",
                  alignItems: "stretch",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: tightEnemyModal ? -20 : compactEnemyModal ? -28 : -40,
                    top: tightEnemyModal ? 22 : compactEnemyModal ? 28 : 34,
                    width: tightEnemyModal
                      ? 118
                      : compactEnemyModal
                        ? 130
                        : 142,
                    height: tightEnemyModal
                      ? 224
                      : compactEnemyModal
                        ? 246
                        : 270,
                    backgroundImage: `url(${enemyUiSrc})`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    backgroundSize: "contain",
                    filter: "drop-shadow(0 10px 24px rgba(0,0,0,0.5))",
                    zIndex: 3,
                    pointerEvents: "none",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "30%",
                      left: "51%",
                      transform: "translate(-50%, -50%)",
                      color: "#fff4da",
                      fontFamily: FONT_FAMILY,
                      fontWeight: 700,
                      fontSize: 34,
                      textShadow: "0 2px 8px rgba(0,0,0,0.95)",
                    }}
                  >
                    {Math.floor(Number(enemy.hp) || 0)}
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      bottom: "17%",
                      left: "51%",
                      transform: "translateX(-50%)",
                      color: "#fff4da",
                      fontFamily: FONT_FAMILY,
                      fontWeight: 700,
                      fontSize: 32,
                      textShadow: "0 2px 8px rgba(0,0,0,0.95)",
                    }}
                  >
                    {Math.floor(Number(enemy.armor) || 0)}
                  </div>
                </div>

                <div
                  style={{
                    borderRadius: tightEnemyModal
                      ? 24
                      : compactEnemyModal
                        ? 27
                        : 31,
                    overflow: "hidden",
                    background: "#000",
                    boxShadow: tightEnemyModal
                      ? "0 14px 34px rgba(0,0,0,0.42)"
                      : compactEnemyModal
                        ? "0 16px 38px rgba(0,0,0,0.44)"
                        : "0 18px 42px rgba(0,0,0,0.46)",
                    width: "100%",
                  }}
                >
                  <img
                    src={enemy.image}
                    alt={enemy.name}
                    style={{ width: "100%", display: "block" }}
                  />
                </div>
              </div>

              <div
                style={{
                  minWidth: 0,
                  paddingTop: 0,
                  maxWidth: tightEnemyModal
                    ? 470
                    : compactEnemyModal
                      ? 510
                      : 560,
                  minHeight: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <div>
                  <div
                    style={{
                      color: "#f4f0e8",
                      fontFamily: FONT_FAMILY,
                      fontWeight: 700,
                      fontSize: "clamp(22px, 1.7vw, 32px)",
                      lineHeight: 1.02,
                      marginBottom: 14,
                      textShadow: "0 2px 8px rgba(0,0,0,0.92)",
                    }}
                  >
                    {enemy.numeral} - {enemy.name}
                  </div>
                  <div
                    style={{
                      color: "rgba(244,240,232,0.9)",
                      fontFamily: FONT_FAMILY,
                      fontWeight: 400,
                      fontSize: "clamp(16px, 1.08vw, 20px)",
                      lineHeight: 1.18,
                      maxWidth: tightEnemyModal
                        ? 470
                        : compactEnemyModal
                          ? 500
                          : 540,
                      marginBottom: tightEnemyModal
                        ? 14
                        : compactEnemyModal
                          ? 16
                          : 18,
                      whiteSpace: "pre-wrap",
                      textShadow: "0 2px 8px rgba(0,0,0,0.9)",
                    }}
                  >
                    {enemyDescriptionOf(enemy)}
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: tightEnemyModal
                      ? "repeat(4, minmax(0, 104px))"
                      : compactEnemyModal
                        ? "repeat(4, minmax(0, 114px))"
                        : "repeat(4, minmax(0, 120px))",
                    columnGap: tightEnemyModal
                      ? 8
                      : compactEnemyModal
                        ? 10
                        : 12,
                    rowGap: tightEnemyModal ? 18 : compactEnemyModal ? 22 : 26,
                    alignItems: "end",
                    justifyContent: "start",
                    overflow: "visible",
                    paddingBottom: 0,
                    marginTop: 0,
                  }}
                >
                  {enemy.resolvedCards.map((card: any, index: number) => {
                    const image = cardImageOf(card);
                    const cardKey = `${enemy.id}-${cardIdOf(card) || cardTitleOf(card) || index}`;
                    const hovered = hoveredEnemyMiniCard === cardKey;

                    return (
                      <div
                        key={cardKey}
                        onMouseEnter={() => setHoveredEnemyMiniCard(cardKey)}
                        onMouseLeave={() =>
                          setHoveredEnemyMiniCard((current) =>
                            current === cardKey ? null : current,
                          )
                        }
                        style={{
                          width: tightEnemyModal
                            ? 104
                            : compactEnemyModal
                              ? 114
                              : 132,
                          overflow: "visible",
                          position: "relative",
                          zIndex: hovered ? 3 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            aspectRatio: COMBAT_CARD_ASPECT,
                            borderRadius: 7,
                            overflow: "hidden",
                            background: "transparent",
                            transform: hovered ? "scale(2.35)" : "scale(1)",
                            transformOrigin: "center center",
                            transition:
                              "transform 220ms ease, box-shadow 220ms ease",
                          }}
                        >
                          {image ? (
                            <img
                              src={image}
                              alt={cardTitleOf(card)}
                              draggable={false}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                display: "block",
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                color: "#f4f0e8",
                                fontFamily: FONT_FAMILY,
                                fontWeight: 700,
                                fontSize: 12,
                                lineHeight: 1.02,
                                padding: 10,
                                textAlign: "center",
                              }}
                            >
                              {cardTitleOf(card)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (selectedEntry.kind === "relic") {
      const relic = selectedEntry.entry;
      const canCycleRelics = unlockedRelicEntries.length > 1;
      const relicIndex = Math.max(
        0,
        relicEntries.findIndex((entry) => entry.id === relic.id),
      );

      return (
        <div
          onClick={() => setSelectedEntry(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 60,
            background: "rgba(0,0,0,0.88)",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              position: "relative",
              width: "100%",
              minHeight: "100vh",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: stackedRelicModal
                ? "84px 28px 34px"
                : tightRelicModal
                  ? "20px 74px 28px"
                  : compactRelicModal
                    ? "24px 92px 32px"
                    : "28px 112px 36px",
            }}
          >
            <button
              type="button"
              onClick={() => setSelectedEntry(null)}
              style={{
                position: "fixed",
                top: SCREEN_CORNER_BUTTON_TOP,
                right: SCREEN_CORNER_BUTTON_RIGHT,
                width: SCREEN_CORNER_BUTTON_SIZE,
                minWidth: SCREEN_CORNER_BUTTON_MIN_SIZE,
                aspectRatio: "1 / 1",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: 0,
                zIndex: 5,
              }}
            >
              <img
                src={closeButtonSrc}
                alt="Close relic modal"
                draggable={false}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </button>

            <button
              type="button"
              onClick={() => cycleSelectedRelic(-1)}
              disabled={!canCycleRelics}
              aria-label="Previous relic"
              style={{
                position: "absolute",
                left: stackedRelicModal
                  ? 42
                  : tightRelicModal
                    ? "calc(50% - 384px)"
                    : compactRelicModal
                      ? "calc(50% - 445px)"
                      : "calc(50% - 512px)",
                top: "50%",
                transform: stackedRelicModal
                  ? "translateY(-50%)"
                  : "translate(-100%, -50%)",
                width: stackedRelicModal
                  ? 34
                  : tightRelicModal
                    ? 42
                    : compactRelicModal
                      ? 50
                      : 58,
                height: stackedRelicModal
                  ? 58
                  : tightRelicModal
                    ? 72
                    : compactRelicModal
                      ? 84
                      : 96,
                border: "none",
                background: "transparent",
                padding: stackedRelicModal
                  ? "10px 18px 10px 10px"
                  : tightRelicModal
                    ? "12px 34px 12px 16px"
                    : compactRelicModal
                      ? "14px 40px 14px 18px"
                      : "16px 48px 16px 22px",
                boxSizing: "content-box",
                cursor: canCycleRelics ? "pointer" : "default",
                opacity: canCycleRelics ? 0.96 : 0.28,
                zIndex: 6,
              }}
            >
              <img
                src={buttonArrowSrc}
                alt=""
                aria-hidden
                draggable={false}
                style={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                  objectFit: "contain",
                }}
              />
            </button>

            <button
              type="button"
              onClick={() => cycleSelectedRelic(1)}
              disabled={!canCycleRelics}
              aria-label="Next relic"
              style={{
                position: "absolute",
                right: stackedRelicModal
                  ? 42
                  : tightRelicModal
                    ? "calc(50% - 384px)"
                    : compactRelicModal
                      ? "calc(50% - 445px)"
                      : "calc(50% - 512px)",
                top: "50%",
                transform: stackedRelicModal
                  ? "translateY(-50%)"
                  : "translate(100%, -50%)",
                width: stackedRelicModal
                  ? 34
                  : tightRelicModal
                    ? 42
                    : compactRelicModal
                      ? 50
                      : 58,
                height: stackedRelicModal
                  ? 58
                  : tightRelicModal
                    ? 72
                    : compactRelicModal
                      ? 84
                      : 96,
                border: "none",
                background: "transparent",
                padding: stackedRelicModal
                  ? "10px 10px 10px 18px"
                  : tightRelicModal
                    ? "12px 16px 12px 34px"
                    : compactRelicModal
                      ? "14px 18px 14px 40px"
                      : "16px 22px 16px 48px",
                boxSizing: "content-box",
                cursor: canCycleRelics ? "pointer" : "default",
                opacity: canCycleRelics ? 0.96 : 0.28,
                zIndex: 6,
              }}
            >
              <img
                src={buttonArrowSrc}
                alt=""
                aria-hidden
                draggable={false}
                style={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                  objectFit: "contain",
                  transform: "scaleX(-1)",
                }}
              />
            </button>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: stackedRelicModal
                  ? "minmax(0, 1fr)"
                  : tightRelicModal
                    ? "minmax(240px, 300px) minmax(360px, 430px)"
                    : compactRelicModal
                      ? "minmax(270px, 340px) minmax(430px, 520px)"
                      : "minmax(300px, 390px) minmax(500px, 610px)",
                gap: stackedRelicModal ? 14 : tightRelicModal ? 16 : 24,
                alignItems: "center",
                justifyContent: "center",
                width: "min(100%, 1020px)",
              }}
            >
              <div
                style={{
                  position: "relative",
                  minHeight: stackedRelicModal
                    ? 220
                    : tightRelicModal
                      ? 270
                      : 320,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    width: stackedRelicModal
                      ? 170
                      : tightRelicModal
                        ? 205
                        : compactRelicModal
                          ? 245
                          : 275,
                    height: stackedRelicModal
                      ? 170
                      : tightRelicModal
                        ? 205
                        : compactRelicModal
                          ? 245
                          : 275,
                    borderRadius: "50%",
                    background:
                      "radial-gradient(circle, rgba(255,196,82,0.18) 0%, rgba(255,160,40,0.08) 34%, rgba(0,0,0,0) 74%)",
                    filter: "blur(18px)",
                    transform: "translateY(8%)",
                    pointerEvents: "none",
                  }}
                />
                <img
                  src={relic.image}
                  alt={relic.name}
                  draggable={false}
                  style={{
                    position: "relative",
                    width: stackedRelicModal ? "min(44vw, 205px)" : "100%",
                    maxWidth: stackedRelicModal
                      ? 205
                      : tightRelicModal
                        ? 210
                        : compactRelicModal
                          ? 250
                          : 285,
                    maxHeight: stackedRelicModal ? 205 : "min(42vh, 335px)",
                    objectFit: "contain",
                    filter: "drop-shadow(0 18px 36px rgba(0,0,0,0.62))",
                    userSelect: "none",
                  }}
                />
              </div>

              <div
                style={{
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: stackedRelicModal ? "center" : "flex-start",
                  textAlign: stackedRelicModal ? "center" : "left",
                }}
              >
                <div
                  style={{
                    color: "#f4f0e8",
                    fontFamily: FONT_FAMILY,
                    fontWeight: 700,
                    fontSize: stackedRelicModal
                      ? "clamp(22px, 4.6vw, 32px)"
                      : "clamp(22px, 1.7vw, 32px)",
                    lineHeight: 1.02,
                    marginBottom: 12,
                    textShadow: "0 2px 8px rgba(0,0,0,0.92)",
                  }}
                >
                  {relic.numeral} - {relic.name}
                </div>

                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: tightRelicModal ? "7px 14px 4px" : "8px 16px 5px",
                    borderRadius: 999,
                    border: "1px solid rgba(255,208,104,0.82)",
                    color: "#ffd866",
                    fontFamily: FONT_FAMILY,
                    fontWeight: 700,
                    fontSize: tightRelicModal ? 16 : 18,
                    lineHeight: 1,
                    background: "rgba(35,20,0,0.38)",
                    boxShadow: "0 0 0 1px rgba(0,0,0,0.35) inset",
                    marginBottom: tightRelicModal ? 14 : 16,
                  }}
                >
                  {capitalize(relic.rarity)}
                </div>

                <div
                  style={{
                    color: "rgba(244,240,232,0.92)",
                    fontFamily: FONT_FAMILY,
                    fontWeight: 400,
                    fontSize: stackedRelicModal
                      ? "clamp(16px, 2.6vw, 20px)"
                      : "clamp(16px, 1.08vw, 20px)",
                    lineHeight: 1.18,
                    whiteSpace: "pre-wrap",
                    maxWidth: stackedRelicModal
                      ? "min(100%, 640px)"
                      : tightRelicModal
                        ? 420
                        : compactRelicModal
                          ? 500
                          : 560,
                    textShadow: "0 2px 8px rgba(0,0,0,0.9)",
                  }}
                >
                  {relic.description || "No description available."}
                </div>

                <div
                  style={{
                    marginTop: tightRelicModal ? 18 : 22,
                    color: "#f4f0e8",
                    fontFamily: FONT_FAMILY,
                    fontWeight: 700,
                    fontSize: stackedRelicModal
                      ? 20
                      : tightRelicModal
                        ? 22
                        : 26,
                    lineHeight: 1,
                    textShadow: "0 2px 8px rgba(0,0,0,0.95)",
                  }}
                >
                  {relicIndex + 1}/{relicEntries.length}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (selectedEntry.kind === "tarot") {
      const tarot = selectedEntry.entry;
      const canCycleTarot = unlockedTarotEntries.length > 1;
      const compactTarotModal =
        viewportSize.width <= 1600 || viewportSize.height <= 980;
      const tightTarotModal =
        viewportSize.width <= 1260 || viewportSize.height <= 860;
      const stackedTarotModal =
        viewportSize.width <= 980 || viewportSize.height <= 840;
      const tarotIndex = tarotEntries.findIndex((entry) => entry.id === tarot.id);

      const renderTarotOrientationPanel = (
        label: string,
        title: string,
        description: string,
        permanent: boolean,
        reversed = false,
      ) => (
        <div
          style={{
            border: "1px solid rgba(255,208,104,0.34)",
            borderRadius: stackedTarotModal ? 18 : 22,
            background:
              "linear-gradient(180deg, rgba(9,9,12,0.96) 0%, rgba(4,4,6,0.92) 100%)",
            boxShadow: "inset 0 0 0 1px rgba(255,214,102,0.08)",
            padding: stackedTarotModal ? "14px 14px 13px" : tightTarotModal ? "16px 16px 15px" : "18px 18px 16px",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 10,
            }}
          >
            <div
              style={{
                color: reversed ? "#d8c3ff" : "#ffd866",
                fontFamily: FONT_FAMILY,
                fontWeight: 700,
                fontSize: stackedTarotModal ? 17 : tightTarotModal ? 18 : 20,
                lineHeight: 1.04,
                textShadow: "0 2px 8px rgba(0,0,0,0.95)",
              }}
            >
              {label}
            </div>
            {permanent && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "6px 11px 4px",
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.2)",
                  background: "rgba(255,255,255,0.06)",
                  color: "rgba(244,240,232,0.86)",
                  fontFamily: FONT_FAMILY,
                  fontWeight: 700,
                  fontSize: stackedTarotModal ? 12 : 13,
                  lineHeight: 1,
                }}
              >
                Permanent
              </div>
            )}
          </div>

          <div
            style={{
              color: "#f4f0e8",
              fontFamily: FONT_FAMILY,
              fontWeight: 700,
              fontSize: stackedTarotModal ? 17 : tightTarotModal ? 18 : 20,
              lineHeight: 1.08,
              marginBottom: 8,
              textShadow: "0 2px 8px rgba(0,0,0,0.92)",
            }}
          >
            {title}
          </div>

          <div
            style={{
              color: "rgba(244,240,232,0.9)",
              fontFamily: FONT_FAMILY,
              fontWeight: 400,
              fontSize: stackedTarotModal
                ? "clamp(15px, 2.35vw, 18px)"
                : "clamp(15px, 1.02vw, 19px)",
              lineHeight: 1.18,
              textShadow: "0 2px 8px rgba(0,0,0,0.9)",
            }}
          >
            {description || "No effect description available."}
          </div>
        </div>
      );

      return (
        <div
          onClick={() => setSelectedEntry(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 60,
            background: "rgba(0,0,0,0.9)",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              position: "relative",
              width: "100%",
              minHeight: "100vh",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: stackedTarotModal
                ? "84px 24px 30px"
                : tightTarotModal
                  ? "18px 74px 24px"
                  : compactTarotModal
                    ? "22px 92px 28px"
                    : "26px 112px 32px",
            }}
          >
            <button
              type="button"
              onClick={() => setSelectedEntry(null)}
              style={{
                position: "fixed",
                top: SCREEN_CORNER_BUTTON_TOP,
                right: SCREEN_CORNER_BUTTON_RIGHT,
                width: SCREEN_CORNER_BUTTON_SIZE,
                minWidth: SCREEN_CORNER_BUTTON_MIN_SIZE,
                aspectRatio: "1 / 1",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: 0,
                zIndex: 6,
              }}
            >
              <img
                src={closeButtonSrc}
                alt="Close tarot modal"
                draggable={false}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </button>

            <button
              type="button"
              onClick={() => cycleSelectedTarot(-1)}
              disabled={!canCycleTarot}
              aria-label="Previous tarot card"
              style={{
                position: "absolute",
                left: stackedTarotModal
                  ? 22
                  : tightTarotModal
                    ? "calc(50% - 392px)"
                    : compactTarotModal
                      ? "calc(50% - 452px)"
                      : "calc(50% - 520px)",
                top: stackedTarotModal ? "33%" : "50%",
                transform: stackedTarotModal
                  ? "translateY(-50%)"
                  : "translate(-100%, -50%)",
                width: stackedTarotModal ? 34 : tightTarotModal ? 42 : compactTarotModal ? 50 : 58,
                height: stackedTarotModal ? 58 : tightTarotModal ? 72 : compactTarotModal ? 84 : 96,
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: canCycleTarot ? "pointer" : "default",
                opacity: canCycleTarot ? 0.96 : 0.28,
                zIndex: 6,
              }}
            >
              <img
                src={buttonArrowSrc}
                alt=""
                aria-hidden
                draggable={false}
                style={{ width: "100%", height: "100%", display: "block", objectFit: "contain" }}
              />
            </button>

            <button
              type="button"
              onClick={() => cycleSelectedTarot(1)}
              disabled={!canCycleTarot}
              aria-label="Next tarot card"
              style={{
                position: "absolute",
                right: stackedTarotModal
                  ? 22
                  : tightTarotModal
                    ? "calc(50% - 392px)"
                    : compactTarotModal
                      ? "calc(50% - 452px)"
                      : "calc(50% - 520px)",
                top: stackedTarotModal ? "33%" : "50%",
                transform: stackedTarotModal
                  ? "translateY(-50%)"
                  : "translate(100%, -50%)",
                width: stackedTarotModal ? 34 : tightTarotModal ? 42 : compactTarotModal ? 50 : 58,
                height: stackedTarotModal ? 58 : tightTarotModal ? 72 : compactTarotModal ? 84 : 96,
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: canCycleTarot ? "pointer" : "default",
                opacity: canCycleTarot ? 0.96 : 0.28,
                zIndex: 6,
              }}
            >
              <img
                src={buttonArrowSrc}
                alt=""
                aria-hidden
                draggable={false}
                style={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                  objectFit: "contain",
                  transform: "scaleX(-1)",
                }}
              />
            </button>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: stackedTarotModal
                  ? "minmax(0, 1fr)"
                  : tightTarotModal
                    ? "minmax(240px, 300px) minmax(380px, 470px)"
                    : compactTarotModal
                      ? "minmax(270px, 340px) minmax(460px, 560px)"
                      : "minmax(300px, 380px) minmax(500px, 620px)",
                gap: stackedTarotModal ? 18 : tightTarotModal ? 18 : 26,
                alignItems: "center",
                justifyContent: "center",
                width: "min(100%, 1080px)",
              }}
            >
              <div
                style={{
                  position: "relative",
                  minHeight: stackedTarotModal ? 260 : tightTarotModal ? 360 : 420,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    width: stackedTarotModal ? 220 : tightTarotModal ? 270 : 310,
                    height: stackedTarotModal ? 220 : tightTarotModal ? 270 : 310,
                    borderRadius: "50%",
                    background:
                      "radial-gradient(circle, rgba(184,139,255,0.16) 0%, rgba(255,188,96,0.12) 28%, rgba(0,0,0,0) 74%)",
                    filter: "blur(22px)",
                    transform: "translateY(6%)",
                    pointerEvents: "none",
                  }}
                />
                <div
                  style={{
                    width: stackedTarotModal ? "min(68vw, 290px)" : "100%",
                    maxWidth: stackedTarotModal ? 290 : tightTarotModal ? 290 : compactTarotModal ? 330 : 360,
                    background: "#000",
                    borderRadius: stackedTarotModal ? 22 : 24,
                    overflow: "hidden",
                    boxShadow: "0 18px 42px rgba(0,0,0,0.58)",
                  }}
                >
                  <img
                    src={tarot.image}
                    alt={tarot.name}
                    draggable={false}
                    style={{
                      width: "100%",
                      height: "auto",
                      display: "block",
                      objectFit: "cover",
                      userSelect: "none",
                    }}
                  />
                </div>
              </div>

              <div
                style={{
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: stackedTarotModal ? "center" : "flex-start",
                  textAlign: stackedTarotModal ? "center" : "left",
                }}
              >
                <div
                  style={{
                    color: "#f4f0e8",
                    fontFamily: FONT_FAMILY,
                    fontWeight: 700,
                    fontSize: stackedTarotModal
                      ? "clamp(22px, 4.8vw, 34px)"
                      : "clamp(24px, 1.9vw, 34px)",
                    lineHeight: 1.02,
                    marginBottom: 12,
                    textShadow: "0 2px 8px rgba(0,0,0,0.92)",
                  }}
                >
                  {tarot.numeral} — {tarot.name}
                </div>

                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: tightTarotModal ? "7px 14px 4px" : "8px 16px 5px",
                    borderRadius: 999,
                    border: "1px solid rgba(255,208,104,0.82)",
                    color: "#ffd866",
                    fontFamily: FONT_FAMILY,
                    fontWeight: 700,
                    fontSize: tightTarotModal ? 16 : 18,
                    lineHeight: 1,
                    background: "rgba(35,20,0,0.38)",
                    boxShadow: "0 0 0 1px rgba(0,0,0,0.35) inset",
                    marginBottom: tightTarotModal ? 14 : 16,
                  }}
                >
                  Tarot Reading
                </div>

                <div
                  style={{
                    width: "100%",
                    maxWidth: stackedTarotModal ? "min(100%, 680px)" : tightTarotModal ? 470 : 560,
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr)",
                    gap: stackedTarotModal ? 12 : 14,
                  }}
                >
                  {renderTarotOrientationPanel(
                    "Upright",
                    tarot.uprightTitle,
                    tarot.uprightDescription,
                    tarot.uprightPermanent,
                    false,
                  )}
                  {renderTarotOrientationPanel(
                    "Reversed",
                    tarot.reversedTitle,
                    tarot.reversedDescription,
                    tarot.reversedPermanent,
                    true,
                  )}
                </div>

                <div
                  style={{
                    marginTop: tightTarotModal ? 18 : 22,
                    color: "#f4f0e8",
                    fontFamily: FONT_FAMILY,
                    fontWeight: 700,
                    fontSize: stackedTarotModal ? 20 : tightTarotModal ? 22 : 26,
                    lineHeight: 1,
                    textShadow: "0 2px 8px rgba(0,0,0,0.95)",
                  }}
                >
                  {Math.max(1, tarotIndex + 1)}/{tarotEntries.length}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (selectedEntry.kind === "lore") {
      const loreEntry = selectedEntry.entry;
      const lorePages = Array.isArray(loreEntry.pages) ? loreEntry.pages : [];
      const compactLoreModal =
        viewportSize.width <= 1600 || viewportSize.height <= 980;
      const tightLoreModal =
        viewportSize.width <= 1260 || viewportSize.height <= 860;
      const stackedLoreModal =
        viewportSize.width <= 980 || viewportSize.height <= 860;
      const safeLorePageIndex = Math.max(
        0,
        Math.min(selectedLorePageIndex, Math.max(0, lorePages.length - 1)),
      );
      const activeLorePage =
        lorePages[safeLorePageIndex] ||
        ({
          id: `${loreEntry.id}:empty`,
          label: "Entry",
          text: "",
          displayText: "",
          discovered: false,
          kind: "misc",
        } as LorePage);
      const canCycleLorePages = lorePages.length > 1;
      const currentLoreImage = getLorePageImage(loreEntry, activeLorePage) || loreEntry.image;
      const loreTextBody = activeLorePage.discovered
        ? activeLorePage.displayText || activeLorePage.text
        : "This page has not yet been recovered from the tower.";

      return (
        <div
          onClick={() => setSelectedEntry(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 60,
            background: "rgba(0,0,0,0.9)",
            overflowY: "auto",
            overflowX: "visible",
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              position: "relative",
              width: "100%",
              minHeight: "100vh",
              overflow: "visible",
              padding: stackedLoreModal
                ? "88px 28px 34px"
                : tightLoreModal
                  ? "28px 86px 34px"
                  : compactLoreModal
                    ? "30px 104px 38px"
                    : "34px 120px 42px",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <button
              type="button"
              onClick={() => setSelectedEntry(null)}
              style={{
                position: "fixed",
                top: 22,
                right: 28,
                width: "min(4.2vw, 68px)",
                minWidth: 40,
                aspectRatio: "1 / 1",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: 0,
                zIndex: 6,
              }}
            >
              <img
                src={closeButtonSrc}
                alt="Close lore modal"
                draggable={false}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </button>

            <button
              type="button"
              onClick={() => cycleSelectedLorePage(-1)}
              disabled={!canCycleLorePages}
              aria-label="Previous lore page"
              style={{
                position: "absolute",
                left: stackedLoreModal
                  ? 10
                  : tightLoreModal
                    ? "calc(50% - 400px)"
                    : compactLoreModal
                      ? "calc(50% - 458px)"
                      : "calc(50% - 520px)",
                top: "50%",
                transform: stackedLoreModal ? "translateY(-50%)" : "translate(-100%, -50%)",
                width: stackedLoreModal ? 34 : tightLoreModal ? 42 : compactLoreModal ? 50 : 58,
                height: stackedLoreModal ? 58 : tightLoreModal ? 72 : compactLoreModal ? 84 : 96,
                border: "none",
                background: "transparent",
                padding: stackedLoreModal
                  ? "10px 18px 10px 10px"
                  : tightLoreModal
                    ? "12px 34px 12px 16px"
                    : compactLoreModal
                      ? "14px 40px 14px 18px"
                      : "16px 48px 16px 22px",
                boxSizing: "content-box",
                cursor: canCycleLorePages ? "pointer" : "default",
                opacity: canCycleLorePages ? 0.96 : 0.28,
                zIndex: 6,
              }}
            >
              <img
                src={buttonArrowSrc}
                alt=""
                aria-hidden
                draggable={false}
                style={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                  objectFit: "contain",
                }}
              />
            </button>

            <button
              type="button"
              onClick={() => cycleSelectedLorePage(1)}
              disabled={!canCycleLorePages}
              aria-label="Next lore page"
              style={{
                position: "absolute",
                right: stackedLoreModal
                  ? 10
                  : tightLoreModal
                    ? "calc(50% - 400px)"
                    : compactLoreModal
                      ? "calc(50% - 458px)"
                      : "calc(50% - 520px)",
                top: "50%",
                transform: stackedLoreModal ? "translateY(-50%)" : "translate(100%, -50%)",
                width: stackedLoreModal ? 34 : tightLoreModal ? 42 : compactLoreModal ? 50 : 58,
                height: stackedLoreModal ? 58 : tightLoreModal ? 72 : compactLoreModal ? 84 : 96,
                border: "none",
                background: "transparent",
                padding: stackedLoreModal
                  ? "10px 10px 10px 18px"
                  : tightLoreModal
                    ? "12px 16px 12px 34px"
                    : compactLoreModal
                      ? "14px 18px 14px 40px"
                      : "16px 22px 16px 48px",
                boxSizing: "content-box",
                cursor: canCycleLorePages ? "pointer" : "default",
                opacity: canCycleLorePages ? 0.96 : 0.28,
                zIndex: 6,
              }}
            >
              <img
                src={buttonArrowSrc}
                alt=""
                aria-hidden
                draggable={false}
                style={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                  objectFit: "contain",
                  transform: "scaleX(-1)",
                }}
              />
            </button>

            <div
              style={{
                width: "min(100%, 980px)",
                display: "grid",
                gridTemplateColumns: stackedLoreModal
                  ? "minmax(0, 1fr)"
                  : tightLoreModal
                    ? "minmax(230px, 290px) minmax(360px, 470px)"
                    : compactLoreModal
                      ? "minmax(250px, 320px) minmax(420px, 540px)"
                      : "minmax(280px, 360px) minmax(480px, 580px)",
                gap: stackedLoreModal ? 18 : tightLoreModal ? 18 : 24,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  position: "relative",
                  minHeight: stackedLoreModal ? 260 : tightLoreModal ? 320 : 360,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    width: stackedLoreModal ? 190 : tightLoreModal ? 220 : 260,
                    height: stackedLoreModal ? 190 : tightLoreModal ? 220 : 260,
                    borderRadius: "50%",
                    background:
                      "radial-gradient(circle, rgba(255,210,120,0.18) 0%, rgba(255,186,88,0.08) 34%, rgba(0,0,0,0) 74%)",
                    filter: "blur(18px)",
                    transform: "translateY(6%)",
                    pointerEvents: "none",
                  }}
                />

                {currentLoreImage ? (
                  <img
                    src={currentLoreImage}
                    alt={loreEntry.title}
                    draggable={false}
                    style={{
                      position: "relative",
                      width: stackedLoreModal ? "min(58vw, 260px)" : "100%",
                      maxWidth: stackedLoreModal
                        ? 260
                        : tightLoreModal
                          ? 250
                          : compactLoreModal
                            ? 292
                            : 320,
                      maxHeight: stackedLoreModal ? 320 : "min(50vh, 420px)",
                      objectFit: "contain",
                      filter: `${activeLorePage.discovered ? "" : "brightness(0.38) saturate(0.22) opacity(0.78)"} drop-shadow(0 18px 36px rgba(0,0,0,0.62))`.trim(),
                      userSelect: "none",
                    }}
                  />
                ) : null}
              </div>

              <div
                style={{
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: stackedLoreModal ? "center" : "flex-start",
                  textAlign: stackedLoreModal ? "center" : "left",
                }}
              >
                <div
                  style={{
                    color: "#f4f0e8",
                    fontFamily: FONT_FAMILY,
                    fontWeight: 700,
                    fontSize: stackedLoreModal
                      ? "clamp(22px, 5vw, 34px)"
                      : "clamp(24px, 1.9vw, 34px)",
                    lineHeight: 1.02,
                    marginBottom: 10,
                    textShadow: "0 2px 8px rgba(0,0,0,0.92)",
                  }}
                >
                  {loreEntry.numeral} — {loreEntry.title}
                </div>

                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: stackedLoreModal ? "center" : "flex-start",
                    gap: 10,
                    marginBottom: 14,
                  }}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: tightLoreModal ? "7px 14px 4px" : "8px 16px 5px",
                      borderRadius: 999,
                      border: `1px solid ${activeLorePage.discovered ? "rgba(255,208,104,0.82)" : "rgba(255,255,255,0.42)"}`,
                      color: activeLorePage.discovered ? "#ffd866" : "rgba(255,255,255,0.74)",
                      fontFamily: FONT_FAMILY,
                      fontWeight: 700,
                      fontSize: tightLoreModal ? 15 : 17,
                      lineHeight: 1,
                      background: activeLorePage.discovered
                        ? "rgba(35,20,0,0.38)"
                        : "rgba(25,25,28,0.42)",
                      boxShadow: "0 0 0 1px rgba(0,0,0,0.35) inset",
                    }}
                  >
                    {activeLorePage.discovered ? "Recovered" : "Missing"}
                  </div>

                  <div
                    style={{
                      color: "rgba(244,240,232,0.92)",
                      fontFamily: FONT_FAMILY,
                      fontWeight: 700,
                      fontSize: stackedLoreModal ? 18 : tightLoreModal ? 18 : 20,
                      lineHeight: 1.08,
                      textShadow: "0 2px 8px rgba(0,0,0,0.9)",
                    }}
                  >
                    {activeLorePage.label}
                  </div>
                </div>

                {loreEntry.showPartTracker && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: stackedLoreModal ? "center" : "flex-start",
                      gap: 8,
                      marginBottom: 16,
                    }}
                  >
                    {Array.from({ length: loreEntry.trackerCount }).map((_, index) => (
                      <img
                        key={`${loreEntry.id}:tracker:${index}`}
                        src={index < loreEntry.discoveredCount ? loreEntryFullSrc : loreEntryBaseSrc}
                        alt=""
                        aria-hidden
                        draggable={false}
                        style={{
                          width: index === safeLorePageIndex ? 30 : 26,
                          height: index === safeLorePageIndex ? 30 : 26,
                          objectFit: "contain",
                          opacity: index < loreEntry.discoveredCount ? 1 : 0.68,
                          transform: index === safeLorePageIndex ? "translateY(-1px)" : "none",
                          filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.55))",
                          userSelect: "none",
                        }}
                      />
                    ))}
                  </div>
                )}

                <div
                  style={{
                    width: "100%",
                    minWidth: 0,
                    maxWidth: stackedLoreModal ? "min(100%, 680px)" : tightLoreModal ? 440 : 520,
                    color: "rgba(244,240,232,0.94)",
                    fontFamily: FONT_FAMILY,
                    fontWeight: 400,
                    fontSize: stackedLoreModal
                      ? "clamp(16px, 2.6vw, 20px)"
                      : "clamp(16px, 1.08vw, 20px)",
                    lineHeight: 1.22,
                    whiteSpace: "pre-wrap",
                    textShadow: "0 2px 8px rgba(0,0,0,0.9)",
                    opacity: activeLorePage.discovered ? 1 : 0.82,
                  }}
                >
                  {loreTextBody}
                </div>

                <div
                  style={{
                    marginTop: tightLoreModal ? 18 : 22,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: stackedLoreModal ? "center" : "flex-start",
                    gap: 16,
                    color: "#f4f0e8",
                    fontFamily: FONT_FAMILY,
                    fontWeight: 700,
                    fontSize: stackedLoreModal ? 18 : tightLoreModal ? 20 : 24,
                    lineHeight: 1,
                    textShadow: "0 2px 8px rgba(0,0,0,0.95)",
                  }}
                >
                  <span>
                    Page {safeLorePageIndex + 1}/{Math.max(1, lorePages.length)}
                  </span>
                  <span>
                    Recovered {loreEntry.discoveredCount}/{loreEntry.totalCount}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const card = selectedEntry.entry;
    const compactCardModal =
      viewportSize.width <= 1600 || viewportSize.height <= 960;
    const tightCardModal =
      viewportSize.width <= 1260 || viewportSize.height <= 840;
    const stackedCardModal =
      viewportSize.width <= 980 || viewportSize.height <= 880;
    const shortCardModal = viewportSize.height <= 760;
    const unlockedCardEntries = cardEntries.filter((entry) => entry.discovered);
    const canCycleCards = unlockedCardEntries.length > 1;
    const cardKeywordItems = getLegendariumCardKeywordItems(card);
    const totalKeywordCharacters = cardKeywordItems.reduce(
      (sum, item) =>
        sum +
        stripHtmlInline(item.label).length +
        stripHtmlInline(item.description).length,
      0,
    );
    const longestKeywordDescription = cardKeywordItems.reduce(
      (longest, item) =>
        Math.max(longest, stripHtmlInline(item.description).length),
      0,
    );
    const denseCardKeywords =
      cardKeywordItems.length >= 4 ||
      totalKeywordCharacters >= 220 ||
      longestKeywordDescription >= 96;
    const ultraDenseCardKeywords =
      cardKeywordItems.length >= 5 ||
      totalKeywordCharacters >= 340 ||
      longestKeywordDescription >= 150;
    const cardModalGap = ultraDenseCardKeywords
      ? stackedCardModal
        ? 8
        : 10
      : denseCardKeywords
        ? stackedCardModal
          ? 10
          : 12
        : stackedCardModal
          ? 14
          : tightCardModal
            ? 16
            : 20;
    const keywordFontSizePx = shortCardModal
      ? ultraDenseCardKeywords
        ? 10.5
        : denseCardKeywords
          ? 11.25
          : 12
      : stackedCardModal
        ? ultraDenseCardKeywords
          ? 11
          : denseCardKeywords
            ? 11.75
            : 13
        : tightCardModal
          ? ultraDenseCardKeywords
            ? 11.5
            : denseCardKeywords
              ? 12.25
              : 13.5
          : ultraDenseCardKeywords
            ? 12
            : denseCardKeywords
              ? 12.75
              : 14;
    const keywordLineHeight = ultraDenseCardKeywords
      ? 1.02
      : denseCardKeywords
        ? 1.07
        : shortCardModal
          ? 1.08
          : 1.14;
    const keywordRowGapPx = ultraDenseCardKeywords
      ? 2
      : denseCardKeywords
        ? 3
        : shortCardModal
          ? 4
          : stackedCardModal
            ? 5
            : 7;
    const keywordPaddingY = ultraDenseCardKeywords
      ? shortCardModal
        ? 7
        : 8
      : denseCardKeywords
        ? shortCardModal
          ? 8
          : 9
        : shortCardModal
          ? 10
          : stackedCardModal
            ? 11
            : 13;
    const keywordPaddingX = ultraDenseCardKeywords
      ? shortCardModal
        ? 11
        : 12
      : denseCardKeywords
        ? shortCardModal
          ? 12
          : 14
        : shortCardModal
          ? 14
          : stackedCardModal
            ? 16
            : 22;
    const keywordPanelWidthTarget = cardKeywordItems.length
      ? Math.min(
          stackedCardModal
            ? viewportSize.width - 44
            : tightCardModal
              ? viewportSize.width - 148
              : compactCardModal
                ? viewportSize.width - 184
                : viewportSize.width - 224,
          stackedCardModal
            ? ultraDenseCardKeywords
              ? 420
              : denseCardKeywords
                ? 400
                : 360
            : tightCardModal
              ? ultraDenseCardKeywords
                ? 460
                : denseCardKeywords
                  ? 430
                  : 390
              : compactCardModal
                ? ultraDenseCardKeywords
                  ? 500
                  : denseCardKeywords
                    ? 470
                    : 430
                : ultraDenseCardKeywords
                  ? 520
                  : denseCardKeywords
                    ? 490
                    : 450,
        )
      : 0;
    const estimatedKeywordCharsPerLine = keywordPanelWidthTarget
      ? Math.max(18, Math.floor((keywordPanelWidthTarget - keywordPaddingX * 2) / (keywordFontSizePx * 0.56)))
      : 0;
    const estimatedKeywordTextLines = cardKeywordItems.reduce((sum, item) => {
      const labelText = stripHtmlInline(item.label);
      const descriptionText = stripHtmlInline(item.description);
      const combinedLength = `${labelText}: ${descriptionText}`.length;
      return (
        sum +
        Math.max(
          1,
          Math.ceil(combinedLength / Math.max(estimatedKeywordCharsPerLine, 18)),
        )
      );
    }, 0);
    const estimatedKeywordBoxHeight = cardKeywordItems.length
      ? keywordPaddingY * 2 +
        estimatedKeywordTextLines * keywordFontSizePx * keywordLineHeight +
        Math.max(cardKeywordItems.length - 1, 0) * keywordRowGapPx +
        (ultraDenseCardKeywords ? 6 : denseCardKeywords ? 4 : 0)
      : 0;
    const selectedCardMaxWidthByViewport = stackedCardModal
      ? Math.min(viewportSize.width * 0.76, ultraDenseCardKeywords ? 280 : denseCardKeywords ? 292 : 310)
      : tightCardModal
        ? Math.min(viewportSize.width * 0.4, ultraDenseCardKeywords ? 300 : denseCardKeywords ? 314 : 330)
        : compactCardModal
          ? Math.min(viewportSize.width * 0.38, ultraDenseCardKeywords ? 344 : denseCardKeywords ? 362 : 380)
          : Math.min(viewportSize.width * 0.36, ultraDenseCardKeywords ? 388 : denseCardKeywords ? 408 : 430);
    const selectedCardMaxWidthByHeight = Math.max(
      ultraDenseCardKeywords
        ? shortCardModal
          ? 156
          : 184
        : denseCardKeywords
          ? shortCardModal
            ? 170
            : 196
          : shortCardModal
            ? 190
            : 220,
      (viewportSize.height -
        (stackedCardModal ? 146 : tightCardModal ? 72 : compactCardModal ? 84 : 94) -
        estimatedKeywordBoxHeight -
        (cardKeywordItems.length ? cardModalGap : 0)) /
        (420 / 288),
    );
    const selectedCardWidthPx = Math.round(
      Math.max(
        ultraDenseCardKeywords
          ? shortCardModal
            ? 156
            : stackedCardModal
              ? 176
              : 210
          : denseCardKeywords
            ? shortCardModal
              ? 170
              : stackedCardModal
                ? 192
                : 224
            : shortCardModal
              ? 190
              : stackedCardModal
                ? 220
                : 250,
        Math.min(selectedCardMaxWidthByViewport, selectedCardMaxWidthByHeight),
      ),
    );
    const selectedCardWidth = `${selectedCardWidthPx}px`;
    const keywordPanelWidthPx = cardKeywordItems.length
      ? Math.round(
          Math.min(
            keywordPanelWidthTarget,
            Math.max(
              selectedCardWidthPx,
              selectedCardWidthPx +
                (ultraDenseCardKeywords ? 140 : denseCardKeywords ? 88 : 0),
            ),
          ),
        )
      : selectedCardWidthPx;
    const keywordPanelWidth = `${keywordPanelWidthPx}px`;

    return (
      <div
        onClick={() => setSelectedEntry(null)}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 60,
          background: "rgba(0,0,0,0.88)",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <div
          onClick={(event) => event.stopPropagation()}
          style={{
            position: "relative",
            width: "100%",
            minHeight: "100vh",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: stackedCardModal
              ? shortCardModal
                ? "72px 22px 24px"
                : "84px 28px 30px"
              : tightCardModal
                ? "18px 74px 24px"
                : compactCardModal
                  ? "22px 92px 28px"
                  : "26px 112px 32px",
          }}
        >
          <button
            type="button"
            onClick={() => setSelectedEntry(null)}
            style={{
              position: "fixed",
              top: SCREEN_CORNER_BUTTON_TOP,
              right: SCREEN_CORNER_BUTTON_RIGHT,
              width: SCREEN_CORNER_BUTTON_SIZE,
              minWidth: SCREEN_CORNER_BUTTON_MIN_SIZE,
              aspectRatio: "1 / 1",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: 0,
              zIndex: 5,
            }}
          >
            <img
              src={closeButtonSrc}
              alt="Close card modal"
              draggable={false}
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </button>

          <button
            type="button"
            onClick={() => cycleSelectedCard(-1)}
            disabled={!canCycleCards}
            aria-label="Previous card"
            style={{
              position: "absolute",
              left: stackedCardModal
                ? 20
                : tightCardModal
                  ? "calc(50% - 290px)"
                  : compactCardModal
                    ? "calc(50% - 330px)"
                    : "calc(50% - 370px)",
              top: stackedCardModal ? "42%" : "50%",
              transform: stackedCardModal
                ? "translateY(-50%)"
                : "translate(-100%, -50%)",
              width: stackedCardModal
                ? 34
                : tightCardModal
                  ? 42
                  : compactCardModal
                    ? 50
                    : 58,
              height: stackedCardModal
                ? 58
                : tightCardModal
                  ? 72
                  : compactCardModal
                    ? 84
                    : 96,
              border: "none",
              background: "transparent",
              padding: 0,
              cursor: canCycleCards ? "pointer" : "default",
              opacity: canCycleCards ? 0.96 : 0.28,
            }}
          >
            <img
              src={buttonArrowSrc}
              alt=""
              aria-hidden
              draggable={false}
              style={{
                width: "100%",
                height: "100%",
                display: "block",
                objectFit: "contain",
              }}
            />
          </button>

          <button
            type="button"
            onClick={() => cycleSelectedCard(1)}
            disabled={!canCycleCards}
            aria-label="Next card"
            style={{
              position: "absolute",
              right: stackedCardModal
                ? 20
                : tightCardModal
                  ? "calc(50% - 290px)"
                  : compactCardModal
                    ? "calc(50% - 330px)"
                    : "calc(50% - 370px)",
              top: stackedCardModal ? "42%" : "50%",
              transform: stackedCardModal
                ? "translateY(-50%)"
                : "translate(100%, -50%)",
              width: stackedCardModal
                ? 34
                : tightCardModal
                  ? 42
                  : compactCardModal
                    ? 50
                    : 58,
              height: stackedCardModal
                ? 58
                : tightCardModal
                  ? 72
                  : compactCardModal
                    ? 84
                    : 96,
              border: "none",
              background: "transparent",
              padding: 0,
              cursor: canCycleCards ? "pointer" : "default",
              opacity: canCycleCards ? 0.96 : 0.28,
            }}
          >
            <img
              src={buttonArrowSrc}
              alt=""
              aria-hidden
              draggable={false}
              style={{
                width: "100%",
                height: "100%",
                display: "block",
                objectFit: "contain",
                transform: "scaleX(-1)",
              }}
            />
          </button>

          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: cardModalGap,
            }}
          >
            <div
              style={{
                width: selectedCardWidth,
                maxWidth: "100%",
                background: "#000",
                borderRadius: shortCardModal ? 18 : stackedCardModal ? 20 : 22,
                overflow: "hidden",
              }}
            >
              {card.image ? (
                <div
                  style={{
                    background: "#000",
                    borderRadius: shortCardModal ? 18 : stackedCardModal ? 20 : 22,
                    overflow: "hidden",
                    lineHeight: 0,
                  }}
                >
                  <img
                    src={card.image}
                    alt={card.title}
                    draggable={false}
                    style={{
                      width: "102.4%",
                      height: "auto",
                      display: "block",
                      objectFit: "contain",
                      userSelect: "none",
                      background: "transparent",
                      transform: "translate(-1.2%, -0.6%)",
                      clipPath: `inset(0.55% 0.7% 0.9% 0.7% round ${shortCardModal ? 18 : stackedCardModal ? 20 : 22}px)`,
                    }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    aspectRatio: COMBAT_CARD_ASPECT,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 20,
                    background:
                      "linear-gradient(180deg, rgba(45,45,48,1) 0%, rgba(11,11,14,1) 100%)",
                    color: "#f4f0e8",
                    fontFamily: FONT_FAMILY,
                    fontWeight: 700,
                    fontSize: shortCardModal ? 18 : stackedCardModal ? 20 : 26,
                    padding: shortCardModal ? 18 : 24,
                    textAlign: "center",
                  }}
                >
                  {card.title}
                </div>
              )}
            </div>

            {cardKeywordItems.length > 0 && (
              <div
                style={{
                  width: keywordPanelWidth,
                  maxWidth: "100%",
                  display: "flex",
                  justifyContent: "center",
                  color: "#f4f0e8",
                  textShadow: "0 2px 8px rgba(0,0,0,0.95)",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    border: "1.5px solid rgba(233, 183, 33, 0.96)",
                    borderRadius: shortCardModal ? 18 : stackedCardModal ? 20 : 26,
                    background:
                      "linear-gradient(180deg, rgba(8,8,10,0.96) 0%, rgba(4,4,6,0.92) 100%)",
                    boxShadow: "inset 0 0 0 1px rgba(255,214,102,0.12)",
                    padding: `${keywordPaddingY}px ${keywordPaddingX}px ${keywordPaddingY + (ultraDenseCardKeywords ? 0 : 1)}px`,
                    display: "flex",
                    flexDirection: "column",
                    gap: keywordRowGapPx,
                    textAlign: "left",
                    fontFamily: FONT_FAMILY,
                    fontWeight: 400,
                    fontSize: `${keywordFontSizePx}px`,
                    lineHeight: keywordLineHeight,
                  }}
                >
                  {cardKeywordItems.map((item) => (
                    <div key={item.key} style={{ display: "block" }}>
                      <span style={{ fontWeight: 700, color: "#efc53f" }}>
                        {stripHtmlInline(item.label)}:{" "}
                      </span>
                      <span
                        style={{
                          color: "rgba(244,240,232,0.78)",
                          fontWeight: 400,
                        }}
                      >
                        {stripHtmlInline(item.description)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 20000,
        background: "#000",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0,
          pointerEvents: "none",
          background: "none",
        }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          padding: "22px 28px 26px",
        }}
      >
        <div
          style={{
            position: "relative",
            flexShrink: 0,
            paddingRight: 82,
            marginBottom: 28,
          }}
        >
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              alignItems: "flex-end",
              minHeight: "clamp(58px, 5.2vw, 82px)",
            }}
          >
            <div
              aria-hidden
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 3,
                height: 1,
                background: "rgba(255,255,255,0.92)",
                pointerEvents: "none",
                zIndex: 0,
              }}
            />
            {tabs.map(renderTabButton)}
            <div
              aria-hidden
              style={{
                flex: 1,
                minWidth: 24,
              }}
            />
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "min(4.2vw, 68px)",
              minWidth: 40,
              aspectRatio: "1 / 1",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: 0,
            }}
          >
            <img
              src={closeButtonSrc}
              alt="Close Legendarium"
              draggable={false}
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </button>
        </div>

        <div
          ref={contentScrollRef}
          style={{
            flex: 1,
            overflow: "auto",
            paddingRight: 10,
            paddingBottom: 8,
          }}
        >
          {activeTab === "enemies" && renderEnemyGrid()}
          {activeTab === "relics" && renderRelicGrid()}
          {activeTab === "cards" && renderCardsGrid()}
          {activeTab === "tarot" && renderTarotGrid()}
          {activeTab === "lore" && renderLoreGrid()}
        </div>
      </div>

      {renderSelectedModal()}
    </div>
  );
}